import React, { useRef, useEffect, useState, useCallback } from 'react';
import { TypesetterWorkerClient } from '../../workers/TypesetterWorkerClient';
import { PAGE_SIZES, type Line, type CaretPosition } from '../../core/types/Typesetter';
import { fontResolver } from '../../services/FontResolver';
import { DPIPersistenceService } from '../../core/utils/DPIPersistenceService';
import { PageSetupConverter } from '../../utils/PageSetupConverter';
import { Transform } from '../../utils/Transform';
import { EdgeDocsCaret } from './EdgeDocsCaret';
import { ProductionCaretSystem } from './caret/ProductionCaretSystem';
import { useEdgeDocsInput, EditCommand, EditCommandResult } from './EdgeDocsInput';
import { useSharedInput } from './SharedInputContext';
import { ClipboardBridge } from './ClipboardBridge';
import { SelectionRenderer } from './EdgeDocsCanvasFunctionsClassesObjects/selection';
import type { SelectionRange } from './EdgeDocsTypes';
import { createIncrementalDebugLogger, initializeOverlaySettings, drawDebugDashboard as renderDebugDashboard, renderDebugOverlays } from './EdgeDocsCanvasFunctionsClassesObjects/debug';
import { getGlyphCacheStatus, filterUncachedGlyphs, quantizeFontSize, buildGlyphCacheKey, handleRenderError, prepareFontAndDocument, renderGlyphs, logDomDiagnostics, applyLineSafetyFilters, filterLeadingEmptyLines, calculatePageYOffset, adjustLineYPositions, adjustPageLayout, acquireRenderLock, releaseRenderLock, initiateGlyphWarming, validateRenderPrerequisites, createRenderTransform, exposeTransformForDebugging, type RenderStateRefs } from './EdgeDocsCanvasFunctionsClassesObjects/rendering';
import { setupCanvasTestListeners } from './EdgeDocsCanvasFunctionsClassesObjects/test';
import { setupCanvasForRendering } from './EdgeDocsCanvasFunctionsClassesObjects/canvas';
import { createSelectionMouseHandlers, updateCaretPosition, updateSelectionHighlight } from './EdgeDocsCanvasFunctionsClassesObjects/selection';
import { createKeyboardEventSetup } from './EdgeDocsCanvasFunctionsClassesObjects/event';
import { setupDocumentTextSync, setupFontSettingsSync, setupStableInputTextSync } from './EdgeDocsCanvasFunctionsClassesObjects/state';
import { setupWasmInitialization } from './EdgeDocsCanvasFunctionsClassesObjects/wasm';
import { setupCaretAndSelectionRenderer, collectCaretMapEntries, adjustCaretYPositions, isCursorOnPage, isCursorOnPageSimple, validateClickGraphemeIndex, getCursorResolutionDiagnostics, formatLineRanges } from './EdgeDocsCanvasFunctionsClassesObjects/caret';
import { createTextEditingHandler } from './EdgeDocsCanvasFunctionsClassesObjects/input';
import { resolvePageLines, resolvePageLinesFromPropsData } from './EdgeDocsCanvasFunctionsClassesObjects/rendering/pipeline/PageLineResolver';
import { resetErrorCounter } from '../../rendering/recovery';
// ✅ OPTION A: Text utilities for single source of truth
import { 
  findWordBoundaries, 
  findSentenceBoundaries, 
  findParagraphBoundaries,
  createClickDiagnostics,
  logClickDiagnostics,
  validateClickPosition
} from './EdgeDocsCanvasFunctionsClassesObjects/text';

// ============================================================================
// DEBUG CONTROL: Set to false to disable all debug logging in production
// ============================================================================
const DEBUG_ENABLED = false; // 🚀 DISABLED for production performance
const debugLog = DEBUG_ENABLED ? console.log.bind(console) : () => {};

// ============================================================================
// 🔬 RENDER CYCLE INSTRUMENTATION - Diagnose text doubling
// ============================================================================
let __renderCycle = 0;

// ============================================================================
// 🆕 OPTION C: WASM as Single Source of Truth
// All text edits go through WASM and the response is used to update local state.
// This eliminates desync issues and prepares for collaboration architecture.
// ============================================================================
const OPTION_C_ENABLED = true; // ✅ ENABLED: WASM is the single source of truth

// Sentinel value for glyphs with no outline (spaces, controls, etc.)
// Use Symbol.for() to create a global symbol that's the same across all modules
const EMPTY_GLYPH_SENTINEL = Symbol.for('empty-glyph');

/**
 * EdgeDocs Canvas V3 - Basic text rendering canvas
 *
 * Renders document text using WASM typesetting and font rendering.
 */
// ============================================================================
// Shared Input Context Type (imported from SharedInputContext)
// ============================================================================
/**
 * 🚀 PHASE 4: Incremental Canvas Render Callback type
 */
type IncrementalRenderCallback = (options: {
  changedLines?: any[];
  clearCanvas: boolean;
  spillFrontier?: { para_ix: number; line_ix: number } | null;
}) => void;

interface SharedInputContextValue {
  inputHandler: any;
  typesetterClientRef: React.RefObject<TypesetterWorkerClient | null>;
  currentDocumentTextRef: React.MutableRefObject<string>;
  currentSnapshotRef: React.MutableRefObject<any>;
  incrementalPagesRef: React.MutableRefObject<any>;
  lastRenderWasIncrementalRef: React.MutableRefObject<boolean>;
  forceReinitRef: React.MutableRefObject<boolean>;
  setRenderTrigger: React.Dispatch<React.SetStateAction<number>>;
  renderTrigger: number;
  fontFamily: string;
  fontSizePx: number;
  // ✅ OPTION A: Single Source of Truth
  getAuthoritativeText: () => string;
  isWasmTextReady: boolean;
  markWasmTextReady: () => void;
  // 🆕 Cross-page navigation support
  getAllCachedLines: () => Line[];
  getAllCachedCaretPositions: () => CaretPosition[];
  // 🚀 PHASE 4: Canvas render registry
  registerCanvasRenderer?: (pageIndex: number, callback: IncrementalRenderCallback) => void;
  unregisterCanvasRenderer?: (pageIndex: number) => void;
  // 🔥 CRITICAL: Initialize incrementalPagesRef with initial pagination
  initializeIncrementalPages?: (paginationResult: { pages: any[]; total_pages: number }) => void;
  // 🐛 DEBUG: Last edit info for dashboard
  getLastEditInfo?: () => {
    type: 'insert' | 'delete' | 'unknown';
    position: number;
    deleteCount: number;
    insertLength: number;
    deletedParagraphIds: string[];
    prevParagraphCount: number;
    newParagraphCount: number;
    timestamp: number;
  } | null;
}

interface EdgeDocsCanvasV3Props {
  /** Document text content and metadata */
  documentState?: { text: string; pageSetup?: any };
  /** Page width in pixels */
  pageWidth: number;
  /** Page height in pixels */
  pageHeight: number;
  /** Scale factor for zoom/DPI */
  scale?: number;
  /** Page setup for layout calculations */
  pageSetup?: any;
  /** Page index to render (0-based) */
  pageIndex?: number;
  /** CSS class name */
  className?: string;
  /** Callback for WASM initialization status changes */
  onWasmStatusChange?: (status: 'loading' | 'ready' | 'error') => void;
  /** Callback for document text changes from input system */
  onDocumentTextChange?: (newText: string) => void;
  /** Callback for page count changes from WASM */
  onPageCountChange?: (pageCount: number) => void;
  /** PHASE 2.2: Shared WASM worker (optional, creates own if not provided) */
  sharedWasmClient?: TypesetterWorkerClient | null;
  /** PHASE 2.2: Pre-fetched page data from shared pagination (optional) */
  pageData?: any;
  /** PHASE 2.2: Full pagination result (for total_pages, etc.) */
  paginationResult?: any;
  /** PHASE 2.2: Skip worker initialization (use shared worker) */
  skipInitialization?: boolean;
  /** ✅ SHARED INPUT: Context from VirtualPages for shared input handling */
  sharedInputContext?: SharedInputContextValue | null;
}

const EdgeDocsCanvasV3Component: React.FC<EdgeDocsCanvasV3Props> = ({
  documentState,
  pageWidth,
  pageHeight,
  scale = 1,
  pageSetup,
  pageIndex = 0,
  onPageCountChange,
  className = '',
  onWasmStatusChange,
  onDocumentTextChange,
  sharedWasmClient,
  pageData,
  paginationResult,
  skipInitialization = false,
  sharedInputContext: sharedInputContextProp = null
}) => {
  // ============================================================================
  // ✅ SHARED INPUT: Use hook to subscribe to context changes directly
  // This ensures React re-renders the Canvas when the context value changes
  // ============================================================================
  const sharedInputContextFromHook = useSharedInput();
  
  // Use hook if available, otherwise fall back to prop (for standalone usage)
  const sharedInputContext = sharedInputContextFromHook ?? sharedInputContextProp;
  
  // ============================================================================
  // ✅ SHARED INPUT: Determine if we're using shared input or local input
  // When sharedInputContext is provided, use it instead of creating local handler
  // ============================================================================
  const useSharedInputMode = sharedInputContext !== null && sharedInputContext.inputHandler !== null;
  
  // ✅ SHARED INPUT: Get render trigger from shared context when in shared mode
  // This allows the shared context's edit commands to trigger Canvas re-renders
  const sharedRenderTrigger = sharedInputContext?.renderTrigger ?? 0;
  
  // 🐛 DEBUG: Log shared input mode status
  DEBUG_ENABLED && console.log(`📱 [CanvasV3 p${pageIndex}] Shared input mode:`, {
    useSharedInputMode,
    hasContext: sharedInputContext !== null,
    hasHandler: sharedInputContext?.inputHandler !== null,
    sharedRenderTrigger,
    fromHook: sharedInputContextFromHook !== null
  });
  
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const overlayCanvasRef = useRef<HTMLCanvasElement>(null); // ✅ Caret overlay
  const caretRef = useRef<EdgeDocsCaret | null>(null); // ✅ Caret instance
  
  // Use BOTH ref (for cleanup) and state (for React reactivity)
  const typesetterClientRef = useRef<TypesetterWorkerClient | null>(null);
  const [typesetterClient, setTypesetterClient] = useState<TypesetterWorkerClient | null>(null);
  const [isTypesetterReady, setIsTypesetterReady] = useState(false);
  const [layoutId, setLayoutId] = useState<string>('v3-doc');
  const [renderTrigger, setRenderTrigger] = useState(0); // For triggering incremental re-renders

  // Font settings from document metadata
  const [fontFamily, setFontFamily] = useState('Inter');
  const [fontSizePx, setFontSizePx] = useState(14);
  
  // Track if we're currently rendering to prevent render loops
  const isRenderingRef = useRef(false);
  // Ref to hold renderText function for WASM callbacks
  const renderTextRef = useRef<((clearCanvas?: boolean, reason?: string) => Promise<void>) | null>(null);
  // Refs to hold overlay functions for text editing handler
  const renderOverlayRef = useRef<(() => void) | null>(null);
  const drawOverlaysRef = useRef<(() => void) | null>(null);
  // Track the last render timestamp to debounce glyph-triggered re-renders
  const lastRenderTimestampRef = useRef(0);
  // 🔥 CIRCUIT BREAKER: Track consecutive WASM errors to prevent infinite loops
  const consecutiveWasmErrorsRef = useRef(0);
  // ✅ PHASE 2: Track previous page setup to detect changes
  const previousPageSetupRef = useRef<any>(null);
  // ✅ PHASE 1: Removed textPropagationTimeoutRef - no longer needed without debouncing
  // ✅ PHASE 2: Flag to force reinit on next render when page setup changes
  const forceReinitRef = useRef(false);
  // ✅ FIX: Track if last render was incremental (don't clear canvas)
  const lastRenderWasIncrementalRef = useRef(false);
  // Track last rendered text to detect actual content changes vs cursor movement
  const lastRenderedTextRef = useRef<string>('');
  // ✅ FIX: Track last local text update to prevent parent from overwriting
  const lastLocalTextUpdateRef = useRef<string | null>(null);
  
  // ✅ Track current WASM snapshot for caret/input systems
  const currentSnapshotRef = useRef<any>(null);
  // ✅ Track transform for all coordinate conversions (replaces coordinateScale)
  const transformRef = useRef<Transform | null>(null);
  // ✅ Track selection renderer
  const selectionRendererRef = useRef<SelectionRenderer | null>(null);
  // ✅ Track if mouse is currently dragging for selection
  const isSelectingRef = useRef(false);
  // ✅ Track if caret has been initialized (only initialize once, not on every render)
  const caretInitializedRef = useRef(false);
  // ✅ CRITICAL FIX: Track last known cursor position to prevent overwrite on remount
  // When Canvas remounts, inputHandler is recreated with position 0, but we should preserve
  // the last known cursor position that was actually set (via cursor change callback)
  const lastKnownCursorPositionRef = useRef<number | null>(null);
  // 🚀 INCREMENTAL LAYOUT: Track last selection for change bounds calculation
  const lastSelectionRef = useRef<SelectionRange | null>(null);
  // 🚀 INCREMENTAL LAYOUT: Track current document text for diff calculations
  const currentDocumentTextRef = useRef<string>(documentState?.text || '');
  // ✅ CRITICAL FIX: Stable text state for input handler to prevent cursor resets
  // This state only updates when text changes from external sources (not our own React state updates)
  // This prevents useEdgeDocsInput from resetting and cursor from jumping to position 0
  const [stableInputText, setStableInputText] = useState<string>(documentState?.text || '');
  // 🐛 DEBUG OVERLAYS: Track overlay settings for visual debugging
  const overlaySettingsRef = useRef<{
    // Visual overlays
    showCaretMap: boolean;
    showGlyphBoxes: boolean;
    showLineBoxes: boolean;
    showBaselines: boolean;
    showWhitespaceBadges: boolean;
    showBreakMarkers: boolean;
    showDebugDashboard: boolean;
    // Incremental layout monitoring
    showIncrementalLogs: boolean;
    showDeltaDetails: boolean;
    showSnapshotDiff: boolean;
    highlightAffectedParagraphs: boolean;
  }>({
    showCaretMap: false,
    showGlyphBoxes: false,
    showLineBoxes: false,
    showBaselines: false,
    showWhitespaceBadges: false,
    showBreakMarkers: false,
    showDebugDashboard: false,
    showIncrementalLogs: false,
    showDeltaDetails: false,
    showSnapshotDiff: false,
    highlightAffectedParagraphs: false
  });
  // 🐛 DEBUG: Track snapshot version for dashboard
  const snapshotVersionRef = useRef(0);
  // 🐛 DEBUG: Track affected paragraphs for highlighting (Phase 4)
  const affectedParagraphsRef = useRef<Set<string>>(new Set());
  
  // 🆕 PHASE 3: Store last incremental pagination result for use in renderText
  // This allows renderText to use incremental pages instead of calling getVisiblePages
  const incrementalPagesRef = useRef<{
    pages: any[];
    totalPages: number;
    stoppedAtPage: number | null;
    timestamp: number;
  } | null>(null);

  // ✅ SHARED INPUT: Sync local refs from shared context when in shared mode
  // The shared context's handleEditCommand updates its refs, so we need to sync them
  // This useEffect syncs when sharedRenderTrigger changes (after each edit command)
  useEffect(() => {
    debugLog(`📱 [CanvasV3 p${pageIndex}] Sync useEffect triggered:`, {
      useSharedInputMode,
      sharedRenderTrigger,
      hasContext: !!sharedInputContext
    });
    
    if (useSharedInputMode && sharedInputContext) {
      // Sync document text from shared context
      if (sharedInputContext.currentDocumentTextRef.current) {
        const oldText = currentDocumentTextRef.current;
        currentDocumentTextRef.current = sharedInputContext.currentDocumentTextRef.current;
        debugLog(`📱 [CanvasV3 p${pageIndex}] Synced text: ${oldText?.length} → ${currentDocumentTextRef.current?.length}`);
      }
      // Sync incremental pages from shared context  
      if (sharedInputContext.incrementalPagesRef.current) {
        incrementalPagesRef.current = sharedInputContext.incrementalPagesRef.current;
        debugLog(`📱 [CanvasV3 p${pageIndex}] Synced incremental pages:`, incrementalPagesRef.current?.pages?.length);
      }
      // Sync flags from shared context
      forceReinitRef.current = sharedInputContext.forceReinitRef.current;
      lastRenderWasIncrementalRef.current = sharedInputContext.lastRenderWasIncrementalRef.current;
    }
  }, [useSharedInputMode, sharedInputContext, sharedRenderTrigger, pageIndex]);

  // 🔴 DEBUG: Track mount/unmount to prove React identity behavior
  useEffect(() => {
    console.log('🔴🔴🔴 [CanvasV3] MOUNTED - component instance created');
    return () => {
      console.log('🔴🔴🔴 [CanvasV3] UNMOUNTED - component instance destroyed');
    };
  }, []);

  // 🔥 CRITICAL FIX: Initialize incrementalPagesRef with initial pagination result
  // This ensures that the first edit has existing pages to merge with.
  // Without this, the first edit results in existing=0 and only the edited page is kept,
  // causing Home/End keys to stop working because the line ranges are incomplete.
  //
  // 🔥 FIX: Also check incrementalPagesRef.current to detect when the provider remounted
  // and the ref was reset to null. This ensures re-initialization even if paginationResult
  // didn't change.
  const incrementalPagesRefValue = sharedInputContext?.incrementalPagesRef?.current;
  useEffect(() => {
    if (paginationResult?.pages && paginationResult.pages.length > 0 && sharedInputContext?.initializeIncrementalPages) {
      sharedInputContext.initializeIncrementalPages({
        pages: paginationResult.pages,
        total_pages: paginationResult.total_pages
      });
    }
  }, [paginationResult, sharedInputContext, incrementalPagesRefValue]);

  // 🚀 PHASE 4: Track when we just did an incremental render
  // This prevents double-rendering when React's useEffect fires after incremental callback
  const lastIncrementalRenderRef = useRef<number>(0);
  
  // 🚀 PHASE 4: Register incremental render callback with SharedInputContext
  // This allows SharedInputContext to trigger renders directly, bypassing React reconciliation
  //
  // 🔥 PERFORMANCE FIX: Use a ref to store the context to avoid effect churn.
  // The effect should only run on mount/unmount, not when context identity changes.
  // Context identity can change when volatile state (renderTrigger, isWasmTextReady) changes,
  // but that shouldn't cause canvas unregister/register storms.
  const sharedInputContextRef = useRef(sharedInputContext);
  sharedInputContextRef.current = sharedInputContext;
  
  useEffect(() => {
    const ctx = sharedInputContextRef.current;
    if (!ctx?.registerCanvasRenderer) {
      return;
    }
    
    // Create the incremental render callback
    const incrementalRenderCallback: IncrementalRenderCallback = (options) => {
      // Access context via ref to get latest values
      const currentCtx = sharedInputContextRef.current;
      
      DEBUG_ENABLED && console.log(`🚀 [CanvasV3 p${pageIndex}] Incremental render called:`, {
        clearCanvas: options.clearCanvas,
        hasSpillFrontier: !!options.spillFrontier,
        changedLinesCount: options.changedLines?.length
      });
      
      // 🚀 PHASE 4 FIX: Sync incrementalPagesRef from shared context BEFORE rendering
      // This is critical because we're skipping setRenderTrigger, so the useEffect
      // that normally syncs this data won't run. We must sync manually here.
      if (currentCtx?.incrementalPagesRef?.current) {
        incrementalPagesRef.current = currentCtx.incrementalPagesRef.current;
      }
      // Also sync the document text ref
      if (currentCtx?.currentDocumentTextRef?.current) {
        currentDocumentTextRef.current = currentCtx.currentDocumentTextRef.current;
      }
      // Sync flags
      if (currentCtx) {
        lastRenderWasIncrementalRef.current = currentCtx.lastRenderWasIncrementalRef.current;
      }
      
      // Mark that we just did an incremental render
      // This prevents the React-triggered useEffect from double-rendering
      lastIncrementalRenderRef.current = Date.now();
      
      // Call renderText with the clearCanvas flag from options
      // 🔬 FIX: Actually pass the clearCanvas flag! Previously it was ignored,
      // causing incremental renders to NOT clear the canvas, leading to visual doubling.
      // For incremental updates, we MUST clear before redrawing to avoid ghosting.
      // The comment said "preserve existing pixels" but that causes doubling.
      renderTextRef.current?.(true, 'incremental'); // 🔬 ALWAYS clear to prevent visual doubling
    };
    
    // Register with SharedInputContext
    ctx.registerCanvasRenderer(pageIndex, incrementalRenderCallback);
    
    // Unregister on unmount
    return () => {
      // Use the captured ctx, not the ref, for cleanup
      ctx.unregisterCanvasRenderer?.(pageIndex);
    };
  }, [pageIndex]); // 🔥 PERFORMANCE FIX: Only depend on pageIndex, not sharedInputContext

  // 🐛 DEBUG OVERLAYS: Initialize debug settings from storage and event listeners
  // Using extracted utility from DebugSettings.ts (previously duplicated inline)
  useEffect(() => {
    return initializeOverlaySettings({
      overlaySettingsRef,
      drawOverlays
    });
  }, []);

  // Update the ref when typesetterClient state changes (for incremental layout)
  useEffect(() => {
    typesetterClientRef.current = typesetterClient;
  }, [typesetterClient]);
  
  // TEMPORARY: Expose client for Phase 1 testing
useEffect(() => {
  if (typesetterClientRef.current) {
    (window as any).testTypesetterClient = typesetterClientRef.current;
    console.log('✅ TypesetterWorkerClient exposed for testing');
  }
}, [typesetterClientRef.current]);

  /**
   * 🐛 DEBUG: Conditional logging based on overlay settings
   * Provides structured logging for incremental layout operations
   * This is separate from the global debugLog to avoid conflicts
   */
  const incrementalDebugLog = createIncrementalDebugLogger(overlaySettingsRef.current);

  // Update current document text when prop changes
  // ⚠️ CRITICAL: Only update if the parent text is actually different
  // This prevents race conditions where local edits get overwritten by stale parent updates
  setupDocumentTextSync({
    documentState,
    isTypesetterReady,
    typesetterClient,
    currentDocumentTextRef,
    lastLocalTextUpdateRef,
    setRenderTrigger
  });

  // Update font settings from document metadata
  setupFontSettingsSync({
    documentState,
    setFontFamily,
    setFontSizePx
  });

  // ✅ CRITICAL FIX: Update stableInputText only for external changes (not our own updates)
  // This prevents useEdgeDocsInput from resetting and cursor from jumping to position 0
  setupStableInputTextSync({
    documentState,
    lastLocalTextUpdateRef,
    setStableInputText
  });

  // ============================================================================
  // 🆕 OPTION C: Create edit command handler for WASM as Single Source of Truth
  // ============================================================================
  const handleEditCommand = useCallback(async (command: EditCommand): Promise<EditCommandResult> => {
    const client = typesetterClientRef.current;
    if (!client) {
      console.warn('[Option C] TypesetterWorkerClient not available');
      return {
        success: false,
        newText: currentDocumentTextRef.current || '',
        newCursorPosition: 0,
        error: 'TypesetterWorkerClient not available'
      };
    }
    
    try {
      const result = await client.applyEditCommand({
        ...command,
        fontFamily,
        fontWeight: 400,
        fontStyle: 'normal'
      });
      
      if (result.success) {
        // Update local refs with WASM response
        currentDocumentTextRef.current = result.newText;
        
        // Trigger re-render with new pages
        if (result.updatedPages.length > 0) {
          incrementalPagesRef.current = {
            pages: result.updatedPages,
            totalPages: result.totalPages,
            stoppedAtPage: null,
            timestamp: Date.now()
          };
          lastRenderWasIncrementalRef.current = true;
        }
        
        setRenderTrigger(prev => prev + 1);
        
        // Notify parent of text change
        if (onDocumentTextChange) {
          onDocumentTextChange(result.newText);
        }
        
        return {
          success: true,
          newText: result.newText,
          newCursorPosition: result.newCursorPosition
        };
      } else {
        return {
          success: false,
          newText: currentDocumentTextRef.current || '',
          newCursorPosition: command.position,
          error: result.error
        };
      }
    } catch (error: any) {
      console.error('[Option C] Edit command failed:', error);
      return {
        success: false,
        newText: currentDocumentTextRef.current || '',
        newCursorPosition: command.position,
        error: error.message
      };
    }
  }, [fontFamily, onDocumentTextChange, setRenderTrigger]);
  
  // ============================================================================
  // ✅ SHARED INPUT: Use shared input handler from context if available
  // Otherwise, create a local input handler (backwards compatibility)
  // ============================================================================
  
  // Local input handler - only created if NOT using shared input
  const localInputHandler = useEdgeDocsInput(
    stableInputText,
    createTextEditingHandler({
      typesetterClientRef,
      currentDocumentTextRef,
      currentSnapshotRef,
      lastSelectionRef,
      incrementalPagesRef,
      lastRenderWasIncrementalRef,
      forceReinitRef,
      consecutiveWasmErrorsRef,
      lastLocalTextUpdateRef,
      overlaySettingsRef,
      setRenderTrigger,
      setStableInputText,
      setTypesetterClient,
      setIsTypesetterReady,
      documentState,
      fontFamily,
      onDocumentTextChange,
      debugLog,
      incrementalDebugLog,
      renderOverlayRef,
      drawOverlaysRef,
      optionCEnabled: OPTION_C_ENABLED
    }),
    undefined,
    OPTION_C_ENABLED ? {
      enabled: true,
      onEditCommand: handleEditCommand
    } : undefined
  );
  
  // ✅ SHARED INPUT: Use shared handler if available, otherwise use local
  // The shared handler is created at VirtualPages level and passed via context
  const inputHandler = useSharedInputMode && sharedInputContext?.inputHandler 
    ? sharedInputContext.inputHandler 
    : localInputHandler;

  // Initialize WASM typesetter
  setupWasmInitialization({
    skipInitialization,
    sharedWasmClient,
    typesetterClientRef,
    setTypesetterClient,
    setIsTypesetterReady,
    setRenderTrigger,
    onWasmStatusChange,
    renderTextRef
  });

  const renderText = useCallback(async (clearCanvas = true, reason = 'unknown') => {
    const timestamp = Date.now();
    
    // 🔬 RENDER CYCLE INSTRUMENTATION
    const cycleId = ++__renderCycle;
    const cycleTextLen = (currentDocumentTextRef.current || '').length;
    console.log("🔬 RENDER_CYCLE_BEGIN", {
      cycleId,
      pageIndex,
      reason,
      clearCanvas,
      textLen: cycleTextLen,
      timestamp
    });
    
    // ✅ Render guard pattern - prevent concurrent renders
    const renderStateRefs: RenderStateRefs = { isRenderingRef, lastRenderTimestampRef };
    const renderLock = acquireRenderLock(renderStateRefs, timestamp, { enableLogging: DEBUG_ENABLED, pageIndex });
    if (!renderLock.acquired) {
      return;
    }
    
    debugLog(`🎨 [CanvasV3] renderText called at ${timestamp}`, {
      hasCanvas: !!canvasRef.current,
      isTypesetterReady,
      hasClient: !!typesetterClient,
      hasDocumentText: !!(documentState?.text)
    });

    // ✅ Validate render prerequisites (Extracted to RenderPrerequisites.ts)
    const prereqResult = validateRenderPrerequisites(
      { canvas: canvasRef.current, typesetterClient, isTypesetterReady },
      { enableLogging: DEBUG_ENABLED, pageIndex }
    );
    
    if (!prereqResult.valid || !prereqResult.ctx) {
      renderLock.release();
      return;
    }
    
    const canvas = prereqResult.canvas!;
    const ctx = prereqResult.ctx;
    // TypeScript assertion: typesetterClient is guaranteed non-null after validation
    const client = typesetterClient!;

    // ✅ HIGH-DPI CANVAS SETUP (Extracted to CanvasSetupUtilities.ts)
    const dpr = window.devicePixelRatio || 1;
    const { canvasCssWidth, canvasCssHeight } = setupCanvasForRendering(
      { canvas, ctx, pageWidth, pageHeight, scale, dpr },
      clearCanvas,
      pageIndex
    );

                // Render text using WASM typesetter
            // ✅ Use currentDocumentTextRef for latest text (includes local edits)
            // ✅ ALWAYS process text, even if empty, to get correct empty snapshot
            const currentText = currentDocumentTextRef.current;
            if (currentText !== null && currentText !== undefined) {
              const renderText = currentText;

              try {

                debugLog(`🎨 [CanvasV3] Starting WASM rendering for ${renderText.length} characters`);
                debugLog(`🎨 [CanvasV3] Sample text: "${renderText.substring(0, 50)}..."`);

                // Font settings are managed by state and useEffect above
                const fontWeight = 400; // Default for now
                const fontStyle = 'normal'; // Default for now

                // Debug: Check if we have required dependencies
                debugLog(`🎨 [CanvasV3] Rendering dependencies:`, {
                  hasTypesetterClient: !!typesetterClient,
                  isTypesetterReady,
                  fontFamily,
                  fontSize: fontSizePx,
                  originalTextLength: documentState?.text?.length || 0,
                  renderTextLength: renderText.length
                });

                // Prepare font and document for rendering
                const fontPrep = prepareFontAndDocument({
                  renderText,
                  documentState,
                  fontFamily,
                  fontWeight,
                  fontStyle,
                  fontSizePx,
                  fontResolver,
                  debugLog
                });

                if (!fontPrep) {
                  return; // Font validation failed
                }

                const { resolvedFontUid, wasmPageSetup, convertedPageSetup, initInput } = fontPrep;

                // Check glyph operations cache status
                const glyphCacheStatus = getGlyphCacheStatus(3);
                debugLog(`🎨 [CanvasV3] Glyph cache status:`, glyphCacheStatus);

                // PHASE 2.2/3: Use cached page data in priority order:
                // 1. Props-provided pagination result (from shared worker)
                // 2. Incremental pages (from Phase 3 incremental pagination)
                // 3. Fall back to full getVisiblePages
                let snapshot;
                
                // 🆕 PHASE 3: Check for incremental pages first
                const incrementalPages = incrementalPagesRef.current;
                // ✅ FIX: Removed 5-second expiry check. Incremental pages remain valid
                // until replaced by newer data (via Option C edit or full re-init).
                // The old timestamp check was causing valid page data to be discarded
                // when clicking on a page after a few seconds, leading to rendering bugs.
                const hasIncrementalPages = incrementalPages && 
                  incrementalPages.pages.length > 0;
                
                // ✅ CRITICAL FIX: When forceReinitRef is true (e.g., ENTER key pressed),
                // skip ALL caches and force full re-initialization with WASM.
                // This ensures line structure changes are properly laid out.
                const mustForceReinit = forceReinitRef.current;
                if (mustForceReinit) {
                  DEBUG_ENABLED && console.log('🎨 [CanvasV3] ⚡ Force reinit requested - skipping all caches');
                  // Clear incremental pages to ensure fresh layout
                  incrementalPagesRef.current = null;
                }
                
                // ✅ PRIORITY FIX: Check incremental pages FIRST - they represent the latest state
                // from Option C edits in SharedInputContext. Props-provided pagination is stale.
                // 
                // 🔥 CRITICAL FIX: Always trust WASM's incremental page count.
                // The previous validation compared against form feeds in currentDocumentTextRef,
                // but that text is STALE during React's async state update cycle.
                // 
                // When a page break is deleted:
                // 1. WASM returns incrementalTotalPages = 3 (correct)
                // 2. But currentDocumentTextRef still has old text with 3 form feeds
                // 3. expectedPagesFromFormFeeds = 3+1 = 4 (WRONG)
                // 4. 3 >= 4 = false → incremental pages were incorrectly skipped
                // 5. Canvas rendered with stale data, text didn't appear until next keydown
                //
                // WASM is the single source of truth. Trust it.
                const incrementalTotalPages = incrementalPages?.totalPages ?? 0;
                const incrementalPageCountValid = incrementalTotalPages > 0; // Trust WASM if it returned any pages
                
                // ✅ RUST FIX APPLIED: WASM now returns correct page_index values
                // The backend fix in pagination.rs adds start_page_index parameter, so
                // pages now have their actual document page indices, not array positions.
                // We can trust the page_index values from incremental results.
                
                if (!mustForceReinit && hasIncrementalPages && incrementalPageCountValid) {
                  // Priority 1: Incremental pages from SharedInputContext (latest from Option C)
                  // Pages not in the incremental result will fall through to Priority 2 (cached)
                  debugLog(`🎨 [CanvasV3] Page ${pageIndex}: Using incremental pages from SharedInputContext (fresh from Option C)`);
                  
                  const cachedSnapshots = client.getCachedParagraphSnapshots();
                  
                  // 🔬 DEBUG: Log what we got from getCachedParagraphSnapshots
                  console.log(`🔬 [CanvasV3] getCachedParagraphSnapshots returned ${cachedSnapshots.length} snapshots`);
                  for (const snap of cachedSnapshots) {
                    const paraKey = (snap.paragraph_id || snap.snapshot_id || 'unknown').substring(0, 8);
                    const lines = snap.lines ?? [];
                    console.log(`🔬 [CanvasV3] Snapshot ${paraKey}: ${lines.length} lines`, 
                      lines.map((l: any) => `[${l.start_grapheme_index}-${l.end_grapheme_index})`).join(', '));
                  }
                  
                  // ✅ FIX: Find page by page_index property, NOT by array index!
                  // updatedPages is a LIST of affected pages, not a sparse array indexed by page number.
                  // Each element has a page_index property that identifies which page it represents.
                  const thisPageLayout = incrementalPages.pages.find((p: any) => p.page_index === pageIndex);
                  
                  if (thisPageLayout && cachedSnapshots.length > 0) {
                    // Extract only the lines that belong to this specific page
                    const pageLines: typeof cachedSnapshots[0]['lines'] = [];
                    let pageCaretMap: typeof cachedSnapshots[0]['caret_map'] = [];
                    
                    // ✅ FIX: For page 0 of a single-page document with incremental layout,
                    // always use ALL lines from ALL cached snapshots. Incremental layouts from
                    // WASM only return slices for affected lines, which causes text above the
                    // cursor to disappear temporarily until the next full render.
                    // 
                    // This handles:
                    // - Single paragraph: Use all lines from the one snapshot
                    // - Multiple paragraphs on page 0: Use all lines from all snapshots
                    const isSinglePageDoc = incrementalPages.totalPages === 1;
                    const shouldUseAllLinesForPage0 = pageIndex === 0 && isSinglePageDoc;
                    
                    if (shouldUseAllLinesForPage0) {
                      // For single-page documents, use all lines from all cached snapshots
                      // This prevents the "disappearing text" bug during incremental edits
                      for (const snapshot of cachedSnapshots) {
                        if (snapshot?.lines) {
                          pageLines.push(...snapshot.lines);
                        }
                      }
                      
                      // 🛡️ Apply safety filters (Phases 0-3) to clean up stale/overlapping/duplicate lines
                      const committedLen = currentDocumentTextRef.current?.length ?? 0;
                      const safetyResult = applyLineSafetyFilters(pageLines, {
                        committedLength: committedLen,
                        pageIndex: 0,
                        enableLogging: true
                      });
                      
                      // Replace pageLines with cleaned version
                      pageLines.length = 0;
                      pageLines.push(...safetyResult.lines);
                      
                      // 🔬 DEBUG: Log all lines being rendered for single-page document
                      console.log(`🔬 [CanvasV3] Page 0 single-page render:`, {
                        snapshotCount: cachedSnapshots.length,
                        totalLines: pageLines.length,
                        overlapsRemoved: safetyResult.overlapsRemoved,
                        duplicatesRemoved: safetyResult.duplicatesRemoved,
                        staleFiltered: safetyResult.staleFiltered,
                        lineRanges: pageLines.map((l, i) => `L${i}:[${l.start_grapheme_index}-${l.end_grapheme_index})`).join(', '),
                        snapshotIds: cachedSnapshots.map(s => s.snapshot_id?.substring(0, 8))
                      });
                      debugLog(`✅ [CanvasV3] Page 0: Using all ${pageLines.length} lines from ${cachedSnapshots.length} snapshot(s) (single-page incremental fix)`);
                    } else {
                      // Multi-page or non-page-0: Use lines directly from WASM (single source of truth)
                      // 🔥 EXTRACTED TO PageLineResolver.ts - handles both NEW and OLD WASM formats
                      const resolveResult = resolvePageLines(
                        thisPageLayout,
                        cachedSnapshots,
                        {
                          pageIndex,
                          committedLength: currentDocumentTextRef.current?.length ?? 0,
                          enableLogging: DEBUG_ENABLED,
                          debugContext: {
                            totalPages: incrementalPages.totalPages,
                            pagesInArray: incrementalPages.pages?.length ?? 0,
                            allPageIndices: incrementalPages.pages?.map((p: any) => p.page_index) ?? [],
                            timestamp: (incrementalPages as any)?.timestamp
                          }
                        }
                      );
                      
                      pageLines.push(...resolveResult.lines);
                      if (resolveResult.caretMap.length > 0) {
                        pageCaretMap = resolveResult.caretMap;
                      }
                  }
                  
                  // ✅ FIX: Filter out leading empty lines from form feed sequences on pages > 0
                  // Extracted to LeadingEmptyLineFilter.ts - handles \f\n continuation lines
                  filterLeadingEmptyLines(pageLines, {
                    pageIndex,
                    enableLogging: DEBUG_ENABLED,
                    priorityLabel: 'Priority 1'
                  });
                  
                  // ✅ FIX: Adjust baseline_y_fp for page-relative positioning
                  // Extracted to PageLayoutCalculator.ts
                  let pageYOffset = 0;
                  if (pageLines.length > 0 && pageIndex > 0) {
                    const topMarginFp = wasmPageSetup?.margins_fp?.top ?? 0;
                    const offsetResult = calculatePageYOffset(pageLines, {
                      pageIndex,
                      topMarginFp,
                      enableLogging: DEBUG_ENABLED
                    });
                    pageYOffset = offsetResult.pageYOffset;
                    
                    if (offsetResult.needsAdjustment) {
                      const adjustedLines = adjustLineYPositions(pageLines, pageYOffset);
                      pageLines.length = 0;
                      pageLines.push(...adjustedLines);
                    }
                  }
                    
                    // Collect caret maps from all cached snapshots (supports multi-paragraph)
                    // Extracted to CaretMapBuilder.ts
                    if (pageLines.length > 0) {
                      const caretResult = collectCaretMapEntries(cachedSnapshots, pageLines, {
                        pageIndex,
                        enableLogging: DEBUG_ENABLED,
                        handleEmptyLines: false // Priority 1 path uses simpler range check
                      });
                      pageCaretMap = adjustCaretYPositions(caretResult.caretMap, {
                        pageIndex,
                        pageYOffset,
                        enableLogging: DEBUG_ENABLED
                      });
                    }
                    
                    if (pageLines.length > 0) {
                      const pageSnapshot = {
                        ...cachedSnapshots[0],
                        lines: pageLines,
                        caret_map: pageCaretMap,
                        snapshot_id: `page-${pageIndex}-incremental`
                      };
                      
                      snapshot = {
                        snapshot: pageSnapshot,
                        pages: incrementalPages.pages,
                        total_pages: incrementalPages.totalPages
                      };
                      
                      debugLog(`✅ [CanvasV3] Using incremental pages:`, {
                        pageIndex,
                        linesOnThisPage: pageLines.length,
                        totalPages: incrementalPages.totalPages
                      });
                    }
                  }
                  
                  // If we couldn't build a snapshot, fall through to other methods
                  if (!snapshot) {
                    debugLog(`⚠️ [CanvasV3] Could not build incremental snapshot for page ${pageIndex}`);
                  }
                }
                
                if (!snapshot && !mustForceReinit && paginationResult && pageData) {
                  // Priority 2: Props-provided pagination (shared worker)
                  // ✅ EXTRACTED TO PageLineResolver.ts - resolvePageLinesFromPropsData
                  debugLog(`🎨 [CanvasV3] Page ${pageIndex}: Using cached page data from shared pagination (skipping initializeDocument)`);

                  const cachedSnapshots = client.getCachedParagraphSnapshots();
                  // 🔥 FIX: Use incrementalPagesRef.totalPages if available (more up-to-date than paginationResult)
                  // paginationResult.total_pages can be stale after Ctrl+Enter creates new pages
                  const totalPages = incrementalPagesRef.current?.totalPages || paginationResult?.total_pages || 1;

                  if (cachedSnapshots.length > 0 && cachedSnapshots[0]?.lines?.length > 0) {
                    // 🔥 EXTRACTED: Line extraction now handled by PageLineResolver
                    const { lines: pageLines } = resolvePageLinesFromPropsData(
                      pageData,
                      cachedSnapshots,
                      {
                        pageIndex,
                        totalPages,
                        enableLogging: DEBUG_ENABLED
                      }
                    );
                    
                    let pageCaretMap: typeof cachedSnapshots[0]['caret_map'] = [];

                    // ✅ FIX: Filter out leading empty lines from form feed sequences on pages > 0
                    // Extracted to LeadingEmptyLineFilter.ts - handles \f\n continuation lines
                    filterLeadingEmptyLines(pageLines, {
                      pageIndex,
                      enableLogging: DEBUG_ENABLED,
                      priorityLabel: 'Priority 2'
                    });

                    // Extract caret map entries for this page's grapheme range
                    // Extracted to CaretMapBuilder.ts - handles empty lines at end of document
                    if (pageLines.length > 0) {
                      const caretResult = collectCaretMapEntries(cachedSnapshots, pageLines, {
                        pageIndex,
                        enableLogging: true, // Keep instrumentation logging
                        handleEmptyLines: true // Priority 2 path handles empty line edge case
                      });
                      pageCaretMap = caretResult.caretMap;
                    }

                    // ✅ FIX: Adjust Y positions for page-relative rendering
                    // Extracted to PageLayoutCalculator.ts
                    const layoutResult = adjustPageLayout(pageLines, pageCaretMap, {
                      pageIndex,
                      wasmPageSetup,
                      enableLogging: DEBUG_ENABLED
                    });
                    const adjustedPageLines = layoutResult.adjustedLines;
                    const adjustedPageCaretMap = layoutResult.adjustedCaretMap;

                    // Build page-specific snapshot with adjusted coordinates
                    const firstSnapshot = cachedSnapshots[0];
                    const pageSpecificSnapshot = {
                      ...firstSnapshot,
                      lines: adjustedPageLines,
                      caret_map: adjustedPageCaretMap
                    };

                    snapshot = {
                      snapshot: pageSpecificSnapshot,
                      pages: paginationResult?.pages || [pageData],
                      total_pages: totalPages
                    };

                    DEBUG_ENABLED && console.log(`✅ [CanvasV3] Page ${pageIndex}: Using page-specific snapshot:`, {
                      pageLines: pageLines.length,
                      caretMapEntries: pageCaretMap.length,
                      totalPages
                    });
                  } else {
                    // Fallback: try to use pagination result snapshot
                    snapshot = paginationResult;
                    console.warn(`⚠️ [CanvasV3] No cached snapshots available, using pagination result snapshot`);
                  }
                }
                
                // Priority 3: Full pagination (if no cached data available)
                if (!snapshot) {
                  // ✅ FIX: If using shared worker (skipInitialization=true) but paginationResult isn't ready,
                  // wait for VirtualPages to complete its pagination instead of doing our own.
                  // This prevents the "flash of all content on page 1" bug where Canvas renders
                  // with partial pagination data (pageCount=1) before full pagination completes.
                  if (skipInitialization && !paginationResult) {
                    debugLog(`⏭️ [CanvasV3] Page ${pageIndex}: Skipping render - waiting for shared pagination to complete`);
                    renderLock.release();
                    return;
                  }
                  
                  debugLog('🎨 [CanvasV3] Initializing document with:', {
                    textLength: renderText.length,
                    fontFamily,
                    fontUid: resolvedFontUid,
                    forceReinit: forceReinitRef.current
                  });

                  // ✅ PHASE 3: Only initialize if forced or if this is the first init
                  // Incremental layout will handle text changes via applyEditDelta
                  const initResult = await client.initializeDocument(initInput, forceReinitRef.current);

                  // Reset force reinit flag after successful initialization
                  if (forceReinitRef.current) {
                    DEBUG_ENABLED && console.log('🎨 [CanvasV3] ✅ Document reinitialized with new page setup - resetting force reinit flag');
                    forceReinitRef.current = false;
                  }

                  debugLog('🎨 [CanvasV3] Document initialized:', initResult);

                  // Glyph operations will be warmed after layout using visible glyph IDs
                  
                  debugLog(`🎨 [CanvasV3] Page ${pageIndex}: Requesting page data from WASM...`);
                  
                  // Request only this specific page
                  snapshot = await client.getVisiblePages(
                    renderText,
                    wasmPageSetup,
                    [pageIndex, pageIndex], // Request only this page
                    fontFamily,
                    fontSizePx,
                    0, // No lookahead
                    fontWeight,
                    fontStyle as any
                  );
                }
                
                const totalPages = snapshot.total_pages || 1;
                debugLog(`🎨 [CanvasV3] Page ${pageIndex}: Received data (total pages: ${totalPages})`);
                
                // Only page 0 reports total page count to avoid duplicate updates
                if (pageIndex === 0 && onPageCountChange) {
                  onPageCountChange(totalPages);
                }
                
                // ✅ Store snapshot for caret/input systems (local ref)
                currentSnapshotRef.current = snapshot.snapshot;
                // 🐛 Increment snapshot version for debug dashboard
                snapshotVersionRef.current++;
                // 🐛 Trigger dashboard update after snapshot changes
                try { renderOverlay(); } catch {}
                
                // ✅ SHARED INPUT: Update shared context snapshot if this page contains the cursor
                // This allows the shared input handler to access this page's caret_map for positioning
                if (useSharedInputMode && sharedInputContext && snapshot.snapshot?.lines?.length > 0) {
                  const cursorPos = inputHandler.getCurrentState?.()?.cursorPosition ?? 0;
                  const lines = snapshot.snapshot.lines;
                  
                  // Check if cursor is on this page (using simple check - no empty line edge case here)
                  const cursorOnThisPage = isCursorOnPageSimple(cursorPos, lines);
                  
                  if (cursorOnThisPage) {
                    debugLog(`📱 [CanvasV3] Page ${pageIndex} contains cursor (${cursorPos}) - updating shared snapshot`);
                    sharedInputContext.currentSnapshotRef.current = snapshot.snapshot;
                  }
                }
                
                // 🆕 OPTION 1 ARCHITECTURE: Layout providers are now wired at SharedInputContext level
                // with ALL document lines, not per-page lines. This ensures navigation works
                // correctly across all pages. See SharedInputContext.tsx for the wiring.
                // 
                // We still need to mark WASM as ready when snapshot data is available.
                if (snapshot.snapshot && snapshot.snapshot.caret_map && snapshot.snapshot.lines) {
                  debugLog('🎨 [CanvasV3] Page snapshot available:', {
                    linesAvailable: snapshot.snapshot.lines?.length || 0,
                    caretMapAvailable: snapshot.snapshot.caret_map?.length || 0
                  });
                  
                  // ✅ OPTION A: Mark WASM text as ready after successful data wiring
                  // This signals that WASM is now the authoritative source for text
                  sharedInputContext?.markWasmTextReady?.();
                }

        debugLog('🎨 [CanvasV3] Visible pages result:', {
          hasSnapshot: !!snapshot.snapshot,
          snapshotLines: snapshot.snapshot?.lines?.length || 0,
          snapshotSource: snapshot.snapshot?.source,
          pagesCount: snapshot.pages?.length || 0,
          fullSnapshot: snapshot.snapshot
        });

        // Get device scale for canvas transform (WORLD_PX → DEVICE_PX)
        const deviceScale = DPIPersistenceService.getDeviceScale();
        debugLog('🎨 [CanvasV3] Device scaling:', {
          deviceScale,
          canvasWidth: canvas.width,
          canvasHeight: canvas.height
        });

        // Add detailed debugging of what we're getting
        if (snapshot.snapshot?.lines) {
          debugLog('🎨 [CanvasV3] First line details:', snapshot.snapshot.lines[0]);
          debugLog('🎨 [CanvasV3] Line runs:', snapshot.snapshot.lines[0]?.runs);
        }

        // ✅ Warm glyph paths for visible content (extracted to GlyphWarmingManager)
        initiateGlyphWarming(typesetterClient, snapshot.snapshot, {
          fontUid: resolvedFontUid,
          fontSizePx,
          maxGlyphs: 100,
          debugLog,
          onWarmingComplete: () => renderTextRef.current?.(true, 'glyph-warming')
        });

        // Render the text with glyphs available - TextRenderer handles all coordinate scaling
        debugLog('🎨 [CanvasV3] ======= STARTING RENDER PHASE =======');
        debugLog('🎨 [CanvasV3] snapshot exists:', !!snapshot);
        debugLog('🎨 [CanvasV3] snapshot.snapshot exists:', !!snapshot?.snapshot);
        debugLog('🎨 [CanvasV3] snapshot.snapshot.lines exists:', !!snapshot?.snapshot?.lines);
        debugLog('🎨 [CanvasV3] snapshot.snapshot.lines length:', snapshot?.snapshot?.lines?.length || 0);

        if (snapshot.snapshot && snapshot.snapshot.lines) {
          // ✅ UNIFIED COORDINATE TRANSFORM SYSTEM (Extracted to CoordinateTransformFactory.ts)
          // Creates a single Transform object used by glyphs, caret, selections, and hit-testing
          const { transform } = createRenderTransform({
            wasmPageSetup,
            canvasCssWidth,
            canvasCssHeight,
            dpr,
            enableLogging: DEBUG_ENABLED
          });
          
          // ✅ CRITICAL: Store transform for caret/input systems
          transformRef.current = transform;
          
          // 🔬 DIAGNOSTIC: Expose transform to console for click debugging
          exposeTransformForDebugging(transform);
          
          // Render glyphs using the unified transform via GlyphRenderingEngine
          const renderResult = renderGlyphs({
            ctx,
            snapshot: snapshot.snapshot,
            transformScale: transform.scale,
            pageIndex,
            cycleId,
            debugLog,
            enableDiagnostics: true
          });
          
          debugLog(`🎨 [CanvasV3] Successfully rendered ${renderResult.renderedLines}/${renderResult.totalLines} lines using unified transform at ${timestamp}`);
          
          // Log DOM diagnostics for debugging
          logDomDiagnostics(canvas, pageIndex);
          
          // ✅ MANDATORY: Update caret position from shared input handler state
          // The shared inputHandler is the source of truth for cursor position across all pages.
          // Each page checks if the cursor is on its page and shows/hides caret accordingly.
          const currentState = inputHandler.getCurrentState();
          const cursorPosition = currentState.selection.focus;
          
          // ✅ Check if cursor is on THIS page before showing caret
          // Uses isCursorOnPage which handles the edge case of cursor at start of empty last line
          const lines = snapshot.snapshot?.lines || [];
          const cursorResult = isCursorOnPage(cursorPosition, lines);
          const cursorOnThisPage = cursorResult.cursorOnPage;
          
          // 🔬 INSTRUMENTATION: Log detailed caret resolution info
          if (cursorPosition >= 30) { // Only log for positions near Enter key
            console.log(`🔬 [CARET RESOLUTION Page ${pageIndex}]`, getCursorResolutionDiagnostics(cursorPosition, lines, pageIndex));
          }
          
          DEBUG_ENABLED && console.log(`🎯 [CanvasV3 Page ${pageIndex}] Caret update:`, {
            cursorPosition,
            pageStartGrapheme: cursorResult.bounds.pageStartGrapheme,
            pageEndGrapheme: cursorResult.bounds.pageEndGrapheme,
            cursorOnThisPage,
            initialized: caretInitializedRef.current
          });
          
          if (cursorOnThisPage) {
            updateCaretPosition(cursorPosition);
            if (caretRef.current) {
              caretRef.current.show();
            }
          } else {
            // Cursor is not on this page - hide caret
            if (caretRef.current) {
              caretRef.current.hide();
            }
          }
          
          // Mark as initialized
          caretInitializedRef.current = true;
        } else {
          console.warn('🎨 [CanvasV3] No snapshot or lines returned from typesetter', snapshot);
        }
      } catch (error) {
        // 🔥 CIRCUIT BREAKER: Track consecutive errors
        consecutiveWasmErrorsRef.current += 1;

        // Handle render error with appropriate visual fallback
        const shouldStopRendering = handleRenderError({
          ctx,
          error: error as Error,
          consecutiveErrors: consecutiveWasmErrorsRef.current,
          renderText,
          documentTextLength: documentState?.text?.length || 0
        });

        if (shouldStopRendering) {
          // Circuit breaker triggered - stop all further processing
          renderLock.release();
          return;
        }
      }
    } else {
      debugLog('🎨 [CanvasV3] No document text to render');
    }
    
    // Release render lock at the end
    renderLock.release();
    // Reset error counter on successful render
    resetErrorCounter(consecutiveWasmErrorsRef);
    debugLog('🎨 [CanvasV3] ✅ Render complete');
  }, [documentState, pageWidth, pageHeight, scale, typesetterClient, fontFamily]);

  // Re-render when viewport/setup changes or manual trigger (via renderTrigger)
  // The isRenderingRef flag prevents infinite loops by blocking concurrent renders
  // ✅ PHASE 2: Added documentState?.pageSetup to trigger re-render when page setup changes
  // ✅ FIX: Removed documentState?.text - incremental layout handles text changes via renderTrigger
  
  // ✅ THROTTLE: Track last render trigger to prevent rapid re-renders
  const lastRenderTriggerRef = useRef({ trigger: 0, sharedTrigger: 0, timestamp: 0 });
  
  useEffect(() => {
    // ✅ FIX: Throttle renders to prevent render loops
    // Only render if trigger changed AND at least 16ms (60fps) since last render
    const now = Date.now();
    const lastRender = lastRenderTriggerRef.current;
    const triggerChanged = renderTrigger !== lastRender.trigger || sharedRenderTrigger !== lastRender.sharedTrigger;
    const timeSinceLastRender = now - lastRender.timestamp;
    
    // 🚀 PHASE 4: Skip React-triggered render if we just did an incremental render
    // The incremental callback already rendered, so React doesn't need to re-render
    const timeSinceIncrementalRender = now - lastIncrementalRenderRef.current;
    if (timeSinceIncrementalRender < 50) {
      DEBUG_ENABLED && console.log(`🎨 [CanvasV3 p${pageIndex}] 🚀 PHASE 4: Skipping React render (incremental render ${timeSinceIncrementalRender}ms ago)`);
      // Still update the trigger tracking so we don't re-render on next cycle
      lastRenderTriggerRef.current = { 
        trigger: renderTrigger, 
        sharedTrigger: sharedRenderTrigger, 
        timestamp: now 
      };
      return;
    }
    
    if (triggerChanged && timeSinceLastRender < 16) {
      // Skip this render, wait for next frame
      DEBUG_ENABLED && console.log(`🎨 [CanvasV3] ⏳ Throttling render (${timeSinceLastRender}ms since last)`);
      return;
    }
    
    // Update last render tracking
    lastRenderTriggerRef.current = { 
      trigger: renderTrigger, 
      sharedTrigger: sharedRenderTrigger, 
      timestamp: now 
    };
    
    // ✅ FIX: Allow render for empty documents (empty string is valid, only skip if undefined/null)
    // Empty documents still need WASM initialized and caret displayed at position 0
    // The original check prevented WASM timeout errors but also broke empty document editing
    const documentTextIsDefined = currentDocumentTextRef.current !== undefined && currentDocumentTextRef.current !== null;
    const documentStateTextIsDefined = documentState?.text !== undefined && documentState?.text !== null;
    
    if (!documentTextIsDefined && !documentStateTextIsDefined) {
      debugLog('🎨 [CanvasV3] Skipping render - documentState.text is undefined/null');
      return;
    }
    
    // ✅ LOG: Confirm we're proceeding with render (even for empty text)
    debugLog('🎨 [CanvasV3] Proceeding with render:', {
      refTextLength: currentDocumentTextRef.current?.length ?? 0,
      propsTextLength: documentState?.text?.length ?? 0,
      isEmpty: (currentDocumentTextRef.current?.length ?? 0) === 0 && (documentState?.text?.length ?? 0) === 0
    });
    
    if (isTypesetterReady && typesetterClient) {
      // ✅ PHASE 2: Detect page setup changes and force reinitialization
      const currentPageSetup = documentState?.pageSetup;
      const previousPageSetup = previousPageSetupRef.current;
      
      // Check if page setup has changed (deep comparison of key properties)
      const pageSetupChanged = currentPageSetup && previousPageSetup && (
        currentPageSetup.margins?.top !== previousPageSetup.margins?.top ||
        currentPageSetup.margins?.right !== previousPageSetup.margins?.right ||
        currentPageSetup.margins?.bottom !== previousPageSetup.margins?.bottom ||
        currentPageSetup.margins?.left !== previousPageSetup.margins?.left ||
        currentPageSetup.paperSize?.width !== previousPageSetup.paperSize?.width ||
        currentPageSetup.paperSize?.height !== previousPageSetup.paperSize?.height ||
        currentPageSetup.orientation !== previousPageSetup.orientation
      );
      
      if (pageSetupChanged) {
        DEBUG_ENABLED && console.log('🎨 [CanvasV3] 📐 PAGE SETUP CHANGED - Forcing WASM reinitialization:', {
          previous: previousPageSetup,
          current: currentPageSetup
        });
        
        // Invalidate page cache to force full re-layout
        typesetterClient.invalidatePages();
        DEBUG_ENABLED && console.log('🎨 [CanvasV3] ✅ Page cache invalidated');
        
        // Set flag to force document reinitialization on next renderText call
        forceReinitRef.current = true;
        DEBUG_ENABLED && console.log('🎨 [CanvasV3] ✅ Force reinit flag set - document will reinitialize with new page setup');
      }
      
      // Update previous page setup reference
      previousPageSetupRef.current = currentPageSetup;
      
      debugLog('🎨 [CanvasV3] 🔄 Re-render triggered by dependency change:', {
        textLength: currentDocumentTextRef.current.length,
        hasPageSetup: !!documentState?.pageSetup,
        pageSetupChanged,
        pageWidth,
        pageHeight,
        scale,
        renderTrigger
      });
      
      // For text input, avoid clearing to reduce flicker
      // Only clear for significant changes (new document load, etc.)
      const shouldClearCanvas = !lastRenderWasIncrementalRef.current;
      DEBUG_ENABLED && console.log('🎨 [CanvasV3] Rendering with clearCanvas:', shouldClearCanvas);

      // Mark this as an incremental render to avoid clearing on next update
      lastRenderWasIncrementalRef.current = true;

      renderText(shouldClearCanvas, 'useEffect-deps');
    }
  // ✅ SHARED INPUT: Also watch sharedRenderTrigger when in shared input mode
  // This ensures that edit commands from the shared context trigger Canvas re-renders
  }, [documentState?.pageSetup, pageWidth, pageHeight, scale, isTypesetterReady, typesetterClient, renderTrigger, sharedRenderTrigger]);

  // Set renderText ref for WASM callbacks
  renderTextRef.current = renderText;

  // Debug utilities exposed to window for console testing
  React.useEffect(() => {
    (window as any).edgedocsDebug = {
      // Check glyph cache status
      checkGlyphCache: () => {
        const cache = (window as any).__edg_glyphOps;
        debugLog('🎨 Glyph Cache Status:', {
          exists: !!cache,
          size: cache?.size || 0,
          keys: cache ? Array.from(cache.keys()).slice(0, 10) : []
        });
        return { cache, size: cache?.size || 0 };
      },

      // Test glyph rendering for a specific glyph
      testGlyphRender: (gid: number, x: number = 50, y: number = 150) => {
        if (!canvasRef.current) {
          console.error('No canvas available');
          return;
        }

        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        const fontUid = 'Inter-Regular.ttf';
        const sizePx = 14;
        const key = `${fontUid}@${sizePx}:${gid}`;

        debugLog(`🧪 Testing glyph ${gid} at (${x}, ${y})`);

        // Check if glyph is cached
        const cache = (window as any).__edg_glyphOps;
        const ops = cache?.get(key);

        if (!ops) {
          debugLog(`❌ Glyph ${gid} not cached`);
          return;
        }

        // Try to render it
        try {
          // Create Path2D
          const p2d = new Path2D();
          for (const op of ops) {
            if (op.type === 'M') p2d.moveTo(op.x, op.y);
            else if (op.type === 'L') p2d.lineTo(op.x, op.y);
            else if (op.type === 'Q') p2d.quadraticCurveTo(op.x1, op.y1, op.x, op.y);
            else if (op.type === 'C') p2d.bezierCurveTo(op.x1, op.y1, op.x2, op.y2, op.x, op.y);
            else if (op.type === 'Z') p2d.closePath();
          }

          // Render at specified position
          ctx.save();
          ctx.fillStyle = '#0000ff'; // Blue for testing
          ctx.translate(x, y);
          ctx.scale(0.1, -0.1); // Small scale for testing
          ctx.fill(p2d);
          ctx.restore();

          debugLog(`✅ Rendered test glyph ${gid} in blue at (${x}, ${y})`);
        } catch (e) {
          console.error(`❌ Failed to render glyph ${gid}:`, e);
        }
      },

      // Check canvas transform and clear
      checkCanvas: () => {
        if (!canvasRef.current) {
          console.error('No canvas available');
          return;
        }

        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        const transform = ctx.getTransform();
        debugLog('🎨 Canvas Status:', {
          size: `${canvas.width}x${canvas.height}`,
          transform: {
            a: transform.a, b: transform.b, c: transform.c,
            d: transform.d, e: transform.e, f: transform.f
          }
        });

        // Test pixel at center
        try {
          const centerX = Math.floor(canvas.width / 2);
          const centerY = Math.floor(canvas.height / 2);
          const pixel = ctx.getImageData(centerX, centerY, 1, 1).data;
          debugLog(`🎨 Center pixel: [${pixel[0]}, ${pixel[1]}, ${pixel[2]}, ${pixel[3]}]`);
        } catch (e) {
          console.error('Could not read pixel:', e);
        }
      },

      // Manual glyph warming test
      warmGlyph: async (gid: number) => {
        const fontUid = 'Inter-Regular.ttf';
        const sizePx = 14;

        debugLog(`🔥 Manually warming glyph ${gid}...`);

        if (!typesetterClient) {
          console.error('No typesetter client available');
          return;
        }

        return typesetterClient.warmGlyphPathsBin(fontUid, [gid], sizePx)
          .then(result => {
            debugLog(`✅ Manual glyph ${gid} warmed:`, result);
            return result;
          })
          .catch(error => {
            debugLog(`❌ Manual glyph ${gid} warming failed:`, error);
            return null;
          });
      },

      // Force re-render
      forceRerender: () => {
        debugLog('🔄 Forcing re-render...');
        // This will trigger a new render cycle
        if (typeof renderText === 'function') {
          renderText();
        } else {
          console.error('renderText function not available');
        }
      },

      // Debug typesetter initialization
      debugTypesetter: () => {
        debugLog('🔧 ======= TYPESETTER DEBUG =======');
        debugLog('Typesetter client:', typesetterClient);
        debugLog('Is typesetter ready:', isTypesetterReady);
        debugLog('Layout ID:', layoutId);

        if (typesetterClient) {
          debugLog('✅ Typesetter client exists');
          debugLog('Client methods:', Object.getOwnPropertyNames(Object.getPrototypeOf(typesetterClient)));
        } else {
          debugLog('❌ Typesetter client is null');
        }

        debugLog('Document state:', documentState);
        debugLog('Canvas ref exists:', !!canvasRef.current);

        // Check global worker
        const globalWorker = (window as any).__typesetterWorker;
        debugLog('Global worker exists:', !!globalWorker);
        if (globalWorker) {
          debugLog('Worker state:', globalWorker.readyState || 'unknown');
        }

        debugLog('============================');
      },

      // Diagnose empty glyph sentinel system
      diagnoseEmptyGlyphs: () => {
        debugLog('🔍 ======= EMPTY GLYPH SENTINEL DIAGNOSTICS =======');

        const cache = (window as any).__edg_glyphOps;
        if (!cache) {
          debugLog('❌ No glyph cache found');
          return;
        }

        debugLog(`📊 Cache contains ${cache.size} glyphs`);

        // Sample some glyphs to check sentinel system
        const sampleGids = [3, 32, 65, 76, 97]; // control, space, A, l, a
        const fontUid = 'Inter-Regular.ttf';
        const sizePx = 14;
        const quantizeSize = (size: number) => Math.round(size * 64) / 64;
        const sizeKey = quantizeSize(sizePx);

        debugLog('🔎 Checking sample glyphs for sentinel system:');
        sampleGids.forEach(gid => {
          const key = `${fontUid}@${sizeKey}:${gid}`;
          const value = cache.get(key);

          let status = '❓ UNKNOWN';
          if (value === EMPTY_GLYPH_SENTINEL) status = '✅ EMPTY_SENTINEL';
          else if (value === undefined) status = '⏳ UNDEFINED (not cached)';
          else if (Array.isArray(value)) status = `📝 OPERATIONS (${value.length})`;
          else status = `❓ OTHER (${typeof value})`;

          debugLog(`  Glyph ${gid}: ${status}`);
        });

        // Check for common empty glyphs
        debugLog('🔍 Scanning for empty glyphs in cache:');
        const emptyGlyphs: number[] = [];
        const operationGlyphs: number[] = [];
        const undefinedGlyphs: number[] = [];

        for (const [key, value] of cache.entries()) {
          if (key.startsWith(`${fontUid}@${sizeKey}:`)) {
            const gid = parseInt(key.split(':')[1]);
            if (value === EMPTY_GLYPH_SENTINEL) emptyGlyphs.push(gid);
            else if (Array.isArray(value)) operationGlyphs.push(gid);
            else undefinedGlyphs.push(gid);
          }
        }

        debugLog(`  ✅ Empty glyphs: [${emptyGlyphs.slice(0, 10).join(', ')}]${emptyGlyphs.length > 10 ? '...' : ''}`);
        debugLog(`  📝 Operation glyphs: [${operationGlyphs.slice(0, 10).join(', ')}]${operationGlyphs.length > 10 ? '...' : ''}`);
        debugLog(`  ⏳ Undefined glyphs: [${undefinedGlyphs.slice(0, 10).join(', ')}]${undefinedGlyphs.length > 10 ? '...' : ''}`);

        return {
          cacheSize: cache.size,
          emptyGlyphs,
          operationGlyphs,
          undefinedGlyphs
        };
      },

      // Inspect glyph coordinate ranges and scaling
      inspectGlyphCoords: (gid: number = 76) => {
        const fontUid = 'Inter-Regular.ttf';
        const sizePx = 14;
        const key = `${fontUid}@${sizePx}:${gid}`;

        debugLog(`🔍 Inspecting glyph ${gid} coordinates and scaling...`);

        // Check if glyph is cached
        const cache = (window as any).__edg_glyphOps;
        const ops = cache?.get(key);

        if (!ops) {
          debugLog(`❌ Glyph ${gid} not cached`);
          return;
        }

        debugLog(`✅ Found glyph ${gid} with ${ops.length} operations`);

        // Extract coordinates from operations
        const coords: number[] = [];
        for (const op of ops) {
          if (op.type === 'M' || op.type === 'L') {
            coords.push(op.x, op.y);
          } else if (op.type === 'Q') {
            coords.push(op.x1, op.y1, op.x, op.y);
          } else if (op.type === 'C') {
            coords.push(op.x1, op.y1, op.x2, op.y2, op.x, op.y);
          }
        }

        if (coords.length === 0) {
          debugLog(`📝 Glyph ${gid} has no coordinates (control character)`);
          return;
        }

        // Calculate ranges
        const minX = Math.min(...coords.filter((_, i) => i % 2 === 0));
        const maxX = Math.max(...coords.filter((_, i) => i % 2 === 0));
        const minY = Math.min(...coords.filter((_, i) => i % 2 === 1));
        const maxY = Math.max(...coords.filter((_, i) => i % 2 === 1));

        debugLog(`📐 Glyph ${gid} coordinate ranges:`);
        debugLog(`   X: [${minX.toFixed(2)}, ${maxX.toFixed(2)}] (width: ${(maxX - minX).toFixed(2)})`);
        debugLog(`   Y: [${minY.toFixed(2)}, ${maxY.toFixed(2)}] (height: ${(maxY - minY).toFixed(2)})`);

        // Current scaling analysis
        const upem = 2048;
        const sizePxWorld = 14;
        const pxPerUnitWorld = sizePxWorld / upem;
        debugLog(`🔍 Scaling analysis:`);
        debugLog(`   Font size: ${sizePxWorld}px`);
        debugLog(`   Units per em: ${upem}`);
        debugLog(`   pxPerUnitWorld: ${pxPerUnitWorld.toFixed(6)}`);

        const scaledWidth = (maxX - minX) * pxPerUnitWorld;
        const scaledHeight = (maxY - minY) * pxPerUnitWorld;
        debugLog(`   Scaled dimensions: ${scaledWidth.toFixed(2)}px × ${scaledHeight.toFixed(2)}px`);

        // Test different scaling factors
        debugLog(`🧪 Testing different scaling factors:`);
        const testScales = [0.0068359375, 0.1, 1.0, 10.0, 100.0];
        for (const scale of testScales) {
          const testWidth = (maxX - minX) * scale;
          const testHeight = (maxY - minY) * scale;
          debugLog(`   Scale ${scale.toFixed(6)}: ${testWidth.toFixed(2)}px × ${testHeight.toFixed(2)}px`);
        }

        return {
          coords,
          ranges: { minX, maxX, minY, maxY },
          currentScale: pxPerUnitWorld,
          scaledDimensions: { width: scaledWidth, height: scaledHeight }
        };
      },

      // Verify world space coordinate system
      verifyCoordinateSystem: () => {
        debugLog('🌍 ======= WORLD SPACE COORDINATE SYSTEM VERIFICATION =======');

        // Get canvas dimensions
        if (!canvasRef.current) {
          console.error('❌ No canvas available');
          return;
        }

        const canvas = canvasRef.current;
        const rect = canvas.getBoundingClientRect();

        debugLog('📐 Canvas Dimensions:');
        debugLog(`   CSS pixels: ${rect.width} × ${rect.height}`);
        debugLog(`   Device pixels: ${canvas.width} × ${canvas.height}`);
        debugLog(`   Device pixel ratio: ${window.devicePixelRatio}`);

        // World space constants (from actual page setup, not hardcoded)
        // Note: These are calculated from actual document page setup if available
        const worldWidthPx = pageWidth * scale;   // From props
        const worldHeightPx = pageHeight * scale; // From props
        const worldDpi = 96;

        debugLog('🌍 World Space Constants (from actual page setup):');
        debugLog(`   Page size: ${worldWidthPx.toFixed(0)} × ${worldHeightPx.toFixed(0)} pixels`);
        debugLog(`   DPI: ${worldDpi}`);
        debugLog(`   Physical size: ${(worldWidthPx/worldDpi).toFixed(2)}" × ${(worldHeightPx/worldDpi).toFixed(2)}"`);

        // Calculate coordinate transformation
        const canvasCssWidth = rect.width;
        const canvasCssHeight = rect.height;

        const scaleX = canvasCssWidth / worldWidthPx;
        const scaleY = canvasCssHeight / worldHeightPx;
        const coordinateScale = Math.min(scaleX, scaleY);

        debugLog('🔄 Coordinate Transformation:');
        debugLog(`   Scale X: ${scaleX.toFixed(6)} (${canvasCssWidth} / ${worldWidthPx.toFixed(0)})`);
        debugLog(`   Scale Y: ${scaleY.toFixed(6)} (${canvasCssHeight} / ${worldHeightPx.toFixed(0)})`);
        debugLog(`   coordinateScale: ${coordinateScale.toFixed(6)} (min of X,Y scales)`);

        // Test with sample WASM coordinates (calculated from actual page dimensions)
        debugLog('🧪 Sample Coordinate Transformations:');

        const testPoints = [
          { name: 'Page Top-Left', x: 0, y: 0 },
          { name: 'Content Area Start', x: 72, y: 72 }, // Approximate margins
          { name: 'Page Center', x: worldWidthPx / 2, y: worldHeightPx / 2 },
          { name: 'Content Area End', x: worldWidthPx - 72, y: worldHeightPx - 72 },
        ];

        testPoints.forEach(point => {
          const canvasX = point.x * coordinateScale;
          const canvasY = point.y * coordinateScale;
          debugLog(`   ${point.name}: (${point.x}, ${point.y}) world px → (${canvasX.toFixed(2)}, ${canvasY.toFixed(2)}) CSS px`);
        });

        // Check if glyph cache has world-space coordinates
        const glyphCache = (window as any).__edg_glyphOps;
        if (glyphCache && glyphCache.size > 0) {
          debugLog('✅ Glyph Cache Status:');
          debugLog(`   Cache size: ${glyphCache.size} glyphs`);
          const sampleKey = Array.from(glyphCache.keys())[0] as string;
          debugLog(`   Sample key: ${sampleKey}`);
          debugLog(`   Key format: font@size:gid (world px size, not CSS px)`);

          // Verify key contains world-space size (should be like "14" not scaled)
          const sizeMatch = sampleKey.match(/@(\d+(?:\.\d+)?):/);
          if (sizeMatch) {
            const glyphSize = parseFloat(sizeMatch[1]);
            debugLog(`   Glyph size in cache: ${glyphSize}px (world space, unscaled)`);
          }
        } else {
          debugLog('⚠️  Glyph cache empty - run some text first');
        }

        // Verify DPR handling
        debugLog('📱 Device Pixel Ratio Handling:');
        debugLog(`   DPR: ${window.devicePixelRatio}`);
        debugLog(`   Canvas device pixels: ${canvas.width} × ${canvas.height}`);
        debugLog(`   Canvas CSS pixels: ${rect.width} × ${rect.height}`);
        debugLog(`   DPR transform applied: ctx.setTransform(${window.devicePixelRatio}, 0, 0, ${window.devicePixelRatio}, 0, 0)`);

        debugLog('✅ VERIFICATION COMPLETE: World space coordinate system is properly implemented!');
        debugLog('   • WASM outputs 96 DPI world coordinates');
        debugLog('   • coordinateScale transforms world → CSS space');
        debugLog('   • DPR handles device pixel backing store');
        debugLog('   • Glyph cache uses world-space sizes');
        debugLog('   • No hardcoded positions or scales');

        return {
          canvasCss: { width: canvasCssWidth, height: canvasCssHeight },
          worldSpace: { width: worldWidthPx, height: worldHeightPx, dpi: worldDpi },
          coordinateScale,
          dpr: window.devicePixelRatio
        };
      }
    };

    debugLog('🔧 EdgeDocs debug utilities loaded. Use window.edgedocsDebug in console.');
    debugLog('Available functions:');
    debugLog('  diagnoseEmptyGlyphs() - Diagnose empty glyph sentinel system');
    debugLog('  checkGlyphCache() - Show glyph cache status');
    debugLog('  inspectGlyphCoords(gid) - Inspect glyph coordinate ranges and scaling');
    debugLog('  testGlyphRender(gid, x, y) - Render test glyph at position');
    debugLog('  checkCanvas() - Show canvas state and pixel data');
    debugLog('  warmGlyph(gid) - Manually warm a glyph');
    debugLog('  forceRerender() - Trigger a re-render cycle');
    debugLog('  debugTypesetter() - Debug WASM typesetter initialization');
    debugLog('  verifyCoordinateSystem() - CONFIRM WORLD SPACE IS WORKING');
  }, []);

  // ✅ CRITICAL FIX: Store inputHandler in a ref so drawCaretForBlink can access it
  // without having it as a dependency (which would cause identity churn)
  const inputHandlerRef = useRef(inputHandler);
  inputHandlerRef.current = inputHandler; // Always keep ref current
  
  // ✅ Draw caret only (called on blink) - SMART CLEAR (doesn't touch selection)
  // BUT: If there's an active selection, we need to redraw it too, or the caret clear will punch a hole in it
  // 
  // ✅ CRITICAL FIX: Use empty dependency array with refs for all mutable values
  // This ensures drawCaretForBlink identity NEVER changes, preventing:
  // 1. setupCaretAndSelectionRenderer's useEffect from re-running
  // 2. Caret from being disposed on every render
  // 3. Caret disappearing when typing starts
  const drawCaretForBlink = useCallback(() => {
    const caret = caretRef.current;
    const overlay = overlayCanvasRef.current;
    const handler = inputHandlerRef.current; // Access via ref

    if (!caret || !overlay || !handler) return;

    // ✅ Check if there's an active selection
    const state = handler.getCurrentState();
    const hasSelection = state.selection && state.selection.anchor !== state.selection.focus;

    // If there's a selection, we can't use smart clearing (would punch hole in selection)
    // Instead, redraw everything
    if (hasSelection) {
      renderOverlayRef.current?.();
      return;
    }

    // No selection - safe to use smart clearing
    const ctx = overlay.getContext('2d');
    if (!ctx) return;

    const scale = caret['scale'];
    const dpr = caret['dpr'];

    // ✅ Clear ONLY the previous caret position (if any)
    if (lastCaretPosRef.current) {
      const prev = lastCaretPosRef.current;
      ctx.save();
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      // Clear a slightly wider rectangle to account for anti-aliasing
      const clearX = Math.floor((prev.x - 2) * scale * dpr);
      const clearY = Math.floor(prev.y * scale * dpr);
      const clearW = Math.ceil(6 * scale * dpr);
      const clearH = Math.ceil((prev.height + 2) * scale * dpr);
      ctx.clearRect(clearX, clearY, clearW, clearH);
      ctx.restore();
    }

    // Draw new caret (if visible)
    if (caret['isActive'] && caret['visible']) {
      const pos = caret['position'];
      const config = caret['config'];

      ctx.save();
      ctx.setTransform(scale * dpr, 0, 0, scale * dpr, 0, 0);
      ctx.strokeStyle = config.color;
      ctx.lineWidth = config.width;
      ctx.beginPath();
      ctx.moveTo(pos.x, pos.y);
      ctx.lineTo(pos.x, pos.y + pos.height);
      ctx.stroke();
      ctx.restore();

      // Remember position for next clear
      lastCaretPosRef.current = { x: pos.x, y: pos.y, height: pos.height };
    }
  }, []); // ✅ EMPTY DEPS: All values accessed via refs, callback identity never changes

  // 🔴 DEBUG: Track drawCaretForBlink identity to prove it's now stable
  const prevDrawCaretForBlinkRef = useRef<typeof drawCaretForBlink | null>(null);
  
  if (prevDrawCaretForBlinkRef.current !== null && prevDrawCaretForBlinkRef.current !== drawCaretForBlink) {
    console.log('🔴🔴🔴 [CanvasV3] drawCaretForBlink IDENTITY CHANGED - this should NOT happen anymore!');
  } else if (prevDrawCaretForBlinkRef.current === null) {
    console.log('🟢 [CanvasV3] drawCaretForBlink created (first render)');
  }
  prevDrawCaretForBlinkRef.current = drawCaretForBlink;

  // ✅ Initialize caret overlay (but don't show it yet)
  setupCaretAndSelectionRenderer({
    overlayCanvasRef,
    caretRef,
    selectionRendererRef,
    drawCaretForBlink
  });
  
  // ✅ Keyboard event handler
  // When using shared input (from VirtualPages), keyboard is handled at parent level.
  // Only set up local keyboard handler when NOT using shared input (standalone mode).
  useEffect(() => {
    if (useSharedInputMode) {
      // Keyboard is handled by SharedInputProvider at VirtualPages level
      debugLog('📱 [CanvasV3] Using shared input - skipping local keyboard setup');
      return () => {};
    }
    // Standalone mode - set up local keyboard handler
    return createKeyboardEventSetup({ inputHandler })();
  }, [inputHandler, useSharedInputMode]);
  
  // ✅ Track mouse drag start position to distinguish clicks from drags
  const mouseDragStartRef = useRef<{ x: number; y: number; grapheme: number } | null>(null);
  const DRAG_THRESHOLD = 3; // pixels - movement less than this is considered a click
  
  // ✅ Track click timing for double/triple-click detection
  const lastClickTimeRef = useRef<number>(0);
  const lastClickGraphemeRef = useRef<number>(-1);
  const clickCountRef = useRef<number>(0);
  const DOUBLE_CLICK_THRESHOLD = 300; // ms - standard double-click time
  
  // ✅ OPTION A: Word/sentence/paragraph boundary functions imported from ./EdgeDocsCanvasFunctionsClassesObjects/text
  // See: findWordBoundaries, findSentenceBoundaries, findParagraphBoundaries
  
  // ✅ Mouse handlers for text selection
  const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    const snapshot = currentSnapshotRef.current;
    const transform = transformRef.current;
    
    if (!snapshot || !transform) {
      return;
    }
    
    // 🔬 DIAGNOSTIC: Log snapshot lines for click debugging
    // This helps identify if the snapshot has wrong/stale lines for this page
    const snapshotLinesForDiag = snapshot?.lines || [];
    console.log(`🔬 [CLICK DEBUG] Page ${pageIndex} snapshot state:`, {
      linesCount: snapshotLinesForDiag.length,
      firstLineGraphemeRange: snapshotLinesForDiag.length > 0 
        ? `${snapshotLinesForDiag[0]?.start_grapheme_index}-${snapshotLinesForDiag[0]?.end_grapheme_index}` 
        : 'N/A',
      lastLineGraphemeRange: snapshotLinesForDiag.length > 0 
        ? `${snapshotLinesForDiag[snapshotLinesForDiag.length-1]?.start_grapheme_index}-${snapshotLinesForDiag[snapshotLinesForDiag.length-1]?.end_grapheme_index}` 
        : 'N/A',
      allLineRanges: snapshotLinesForDiag.map((l: any, i: number) => `L${i}:[${l.start_grapheme_index}-${l.end_grapheme_index}]`).join(', '),
      caretMapLength: snapshot?.caret_map?.length ?? 0,
      caretMapFirstIdx: snapshot?.caret_map?.[0]?.grapheme_index ?? 'N/A',
      caretMapLastIdx: snapshot?.caret_map?.[snapshot?.caret_map?.length - 1]?.grapheme_index ?? 'N/A',
    });
    
    // Get click position and convert to world coords
    const rect = e.currentTarget.getBoundingClientRect();
    const clickCssX = e.clientX - rect.left;
    const clickCssY = e.clientY - rect.top;
    const clickWorldX = transform.canvasToWorldX(clickCssX);
    const clickWorldY = transform.canvasToWorldY(clickCssY);
    
    // Find grapheme index at click position
    const topMargin = 0;
    let graphemeIndex = ProductionCaretSystem.getGraphemeIndexFromClick(
      snapshot,
      clickWorldX,
      clickWorldY,
      topMargin
    );
    
    // ✅ FIX: Validate grapheme index against page bounds
    // For multi-page documents, getGraphemeIndexFromClick may return a page-local index
    // We need to check if it's plausible for this page and potentially add the page offset
    const snapshotLines = snapshot?.lines || [];
    if (snapshotLines.length > 0) {
      const validated = validateClickGraphemeIndex(graphemeIndex, snapshotLines, pageIndex);
      if (validated.warning) {
        console.warn(validated.warning);
      }
      graphemeIndex = validated.graphemeIndex;
    }
    
    // ✅ Detect double/triple-click
    const now = Date.now();
    const timeSinceLastClick = now - lastClickTimeRef.current;
    const samePosition = graphemeIndex === lastClickGraphemeRef.current;
    
    if (timeSinceLastClick < DOUBLE_CLICK_THRESHOLD && samePosition) {
      clickCountRef.current += 1;
    } else {
      clickCountRef.current = 1;
    }
    
    lastClickTimeRef.current = now;
    lastClickGraphemeRef.current = graphemeIndex;
    
    debugLog('🖱️ [CanvasV3] Mouse down:', { 
      graphemeIndex, 
      cssPos: `(${clickCssX}, ${clickCssY})`,
      clickCount: clickCountRef.current 
    });
    
    // ✅ OPTION A: Get authoritative text from shared context or WASM
    // This is the SINGLE SOURCE OF TRUTH for document text
    const authoritativeText = sharedInputContext?.getAuthoritativeText?.() 
      || typesetterClientRef.current?.getLastInitializedText?.() 
      || currentDocumentTextRef.current 
      || '';
    const isFromWasm = sharedInputContext?.isWasmTextReady ?? false;
    
    // 🔬 DIAGNOSTIC: Log click diagnostics using authoritative text
    const clickDiagnostics = createClickDiagnostics({
      text: authoritativeText,
      isFromWasm,
      graphemeIndex,
      pageIndex,
      worldX: clickWorldX,
      worldY: clickWorldY
    });
    logClickDiagnostics(clickDiagnostics, 'Character at click position');
    
    // Validate grapheme index against authoritative text
    const positionWarning = validateClickPosition(graphemeIndex, authoritativeText.length);
    if (positionWarning) {
      console.warn(`⚠️ [CLICK] ${positionWarning}`);
    }
    
    // ✅ Handle multi-click selection using authoritative text
    if (clickCountRef.current === 3) {
      // Triple-click → Select sentence
      const bounds = findSentenceBoundaries(authoritativeText, graphemeIndex);
      debugLog('🖱️ [CanvasV3] Triple-click → Select sentence:', bounds);
      inputHandler.setSelection({ anchor: bounds.start, focus: bounds.end });
      updateSelectionHighlight();
      return; // Don't start drag
    } else if (clickCountRef.current === 2) {
      // Double-click → Select word (using authoritative text)
      const bounds = findWordBoundaries(authoritativeText, graphemeIndex);
      debugLog('🖱️ [CanvasV3] Double-click → Select word:', bounds);
      inputHandler.setSelection({ anchor: bounds.start, focus: bounds.end });
      updateSelectionHighlight();
      return; // Don't start drag
    }
    
    // ✅ Single click - start drag tracking
    mouseDragStartRef.current = { x: clickCssX, y: clickCssY, grapheme: graphemeIndex };
    
    // Start selection drag
    isSelectingRef.current = true;
    inputHandler.beginSelectionDrag(graphemeIndex);
    updateCaretPosition(graphemeIndex);
    
    // ✅ FIX: Show caret after click - updateCaretPosition only sets position, doesn't show it
    if (caretRef.current) {
      caretRef.current.show();
    }
  };
  
  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!isSelectingRef.current) {
      return;
    }
    
    const snapshot = currentSnapshotRef.current;
    const transform = transformRef.current;
    
    if (!snapshot || !transform) {
      return;
    }
    
    // Get current mouse position and convert to world coords
    const rect = e.currentTarget.getBoundingClientRect();
    const clickCssX = e.clientX - rect.left;
    const clickCssY = e.clientY - rect.top;
    const clickWorldX = transform.canvasToWorldX(clickCssX);
    const clickWorldY = transform.canvasToWorldY(clickCssY);
    
    // Find grapheme index at current position
    const topMargin = 0;
    let graphemeIndex = ProductionCaretSystem.getGraphemeIndexFromClick(
      snapshot,
      clickWorldX,
      clickWorldY,
      topMargin
    );
    
    // ✅ FIX: Validate grapheme index against page bounds (same fix as handleMouseDown)
    const snapshotLines = snapshot?.lines || [];
    if (snapshotLines.length > 0 && pageIndex > 0) {
      const pageStartGrapheme = snapshotLines[0]?.start_grapheme_index ?? 0;
      if (graphemeIndex < pageStartGrapheme) {
        graphemeIndex += pageStartGrapheme;
      }
    }
    
    // ✅ Enhanced diagnostic logging
    const state = inputHandler.getCurrentState();
    const anchor = state.selection?.anchor ?? graphemeIndex;
    const focus = graphemeIndex;
    const selectionRange = [Math.min(anchor, focus), Math.max(anchor, focus)];
    const direction = focus >= anchor ? 'forward' : 'backward';
    
    DEBUG_ENABLED && console.log('🖱️ [SELECTION] Mouse move:', {
      anchor,
      focus,
      selectionRange: `[${selectionRange[0]}, ${selectionRange[1]}]`,
      direction,
      mousePos: `(${clickCssX.toFixed(0)}, ${clickCssY.toFixed(0)})`,
      worldPos: `(${clickWorldX.toFixed(2)}, ${clickWorldY.toFixed(2)})`
    });
    
    // Update selection drag
    inputHandler.updateSelectionDrag(graphemeIndex);
    updateCaretPosition(graphemeIndex);
    updateSelectionHighlight();
  };
  
  const handleMouseUp = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!isSelectingRef.current) {
      return;
    }
    
    const rect = e.currentTarget.getBoundingClientRect();
    const clickCssX = e.clientX - rect.left;
    const clickCssY = e.clientY - rect.top;
    
    // ✅ Check if this was a drag or a click
    const dragStart = mouseDragStartRef.current;
    if (dragStart) {
      const dx = Math.abs(clickCssX - dragStart.x);
      const dy = Math.abs(clickCssY - dragStart.y);
      const dragDistance = Math.sqrt(dx * dx + dy * dy);
      
      if (dragDistance < DRAG_THRESHOLD) {
        // This was a click, not a drag - collapse selection to click point
        debugLog('🖱️ [CanvasV3] Mouse up - detected click (no drag), collapsing selection');
        inputHandler.setCursorPosition(dragStart.grapheme);
        updateCaretPosition(dragStart.grapheme);
        updateSelectionHighlight(); // Clear selection
        // ✅ FIX: Show caret after click
        if (caretRef.current) {
          caretRef.current.show();
        }
      } else {
        // This was a drag - keep the selection
        debugLog('🖱️ [CanvasV3] Mouse up - detected drag, finalizing selection');
        inputHandler.endSelectionDrag();
        updateSelectionHighlight(); // Keep selection
      }
    } else {
      // Fallback: end drag
      debugLog('🖱️ [CanvasV3] Mouse up - no drag start tracked, ending selection');
      inputHandler.endSelectionDrag();
      updateSelectionHighlight();
    }
    
    isSelectingRef.current = false;
    mouseDragStartRef.current = null;
  };
  
  // ✅ CLIPBOARD HANDLERS: Used by ClipboardBridge for native clipboard events
  const handlePasteText = useCallback((text: string) => {
    debugLog('📋 [CanvasV3] Paste received:', { textLength: text.length, textPreview: text.slice(0, 50) });
    
    // ✅ Use inputHandler.pasteText() which properly handles Option C
    // This sends the paste as an edit command to WASM, ensuring proper shaping
    inputHandler.pasteText(text);
    
    debugLog('📋 [CanvasV3] Paste dispatched to inputHandler');
  }, [inputHandler]);
  
  const getSelectedText = useCallback((): string => {
    const currentState = inputHandler.getCurrentState();
    const { start, end } = { 
      start: Math.min(currentState.selection.anchor, currentState.selection.focus),
      end: Math.max(currentState.selection.anchor, currentState.selection.focus)
    };
    if (start === end) return '';
    const selected = currentState.text.slice(start, end);
    debugLog('📋 [CanvasV3] Get selected text:', { start, end, length: selected.length });
    return selected;
  }, [inputHandler]);
  
  const handleCutSelection = useCallback(() => {
    debugLog('📋 [CanvasV3] Cut selection');
    const currentState = inputHandler.getCurrentState();
    const { start, end } = { 
      start: Math.min(currentState.selection.anchor, currentState.selection.focus),
      end: Math.max(currentState.selection.anchor, currentState.selection.focus)
    };
    if (start === end) return;
    
    // Delete the selection
    const newText = currentState.text.slice(0, start) + currentState.text.slice(end);
    inputHandler.setState({
      text: newText,
      selection: { anchor: start, focus: start },
      cursorPosition: start
    });
    debugLog('📋 [CanvasV3] Cut complete:', { newTextLength: newText.length, cursorPos: start });
  }, [inputHandler]);
  
  // ✅ Mouse click handler for caret positioning (kept for compatibility)
  const handleCanvasClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const snapshot = currentSnapshotRef.current;
    const transform = transformRef.current;
    
    if (!snapshot || !transform) {
      debugLog('🖱️ [CanvasV3] Click ignored - missing snapshot or transform');
      return;
    }
    
    // Get click position relative to canvas (in CSS pixels)
    const rect = e.currentTarget.getBoundingClientRect();
    const clickCssX = e.clientX - rect.left;
    const clickCssY = e.clientY - rect.top;
    
    // ✅ Transform click coords from Canvas CSS space to WASM world space
    // ProductionCaretSystem expects world-space coordinates
    const clickWorldX = transform.canvasToWorldX(clickCssX);
    const clickWorldY = transform.canvasToWorldY(clickCssY);
    
    debugLog('🖱️ [CanvasV3] Canvas click:', { 
      cssCoords: `(${clickCssX.toFixed(2)}, ${clickCssY.toFixed(2)})`,
      worldCoords: `(${clickWorldX.toFixed(2)}, ${clickWorldY.toFixed(2)})`,
      transformScale: transform.scale.toFixed(6)
    });
    
    // Use ProductionCaretSystem to find grapheme index from click (world coords)
    const topMargin = 0; // V3 uses absolute positioning
    let graphemeIndex = ProductionCaretSystem.getGraphemeIndexFromClick(
      snapshot,
      clickWorldX,  // ✅ Use world-space X
      clickWorldY,  // ✅ Use world-space Y
      topMargin
    );
    
    // ✅ FIX: Validate grapheme index against page bounds (same fix as handleMouseDown)
    const snapshotLines = snapshot?.lines || [];
    if (snapshotLines.length > 0 && pageIndex > 0) {
      const pageStartGrapheme = snapshotLines[0]?.start_grapheme_index ?? 0;
      if (graphemeIndex < pageStartGrapheme) {
        debugLog(`⚠️ [CanvasV3] Page ${pageIndex}: Click returned local index ${graphemeIndex}, adding page offset ${pageStartGrapheme}`);
        graphemeIndex += pageStartGrapheme;
      }
    }
    
    debugLog('🖱️ [CanvasV3] Grapheme index from click:', graphemeIndex);
    
    // Update cursor position
    inputHandler.setCursorPosition(graphemeIndex);
    updateCaretPosition(graphemeIndex);
  };
  
  // ✅ Update caret position when cursor changes
  const updateCaretPosition = (cursorIndex: number) => {
    const snapshot = currentSnapshotRef.current;
    const transform = transformRef.current;

    if (!snapshot || !caretRef.current || !transform) {
      debugLog('🎯 [CanvasV3] Caret update skipped - missing snapshot, caret, or transform');
      return;
    }

    const topMargin = 0; // V3 uses absolute positioning
    const caretPos = ProductionCaretSystem.getCaretPosition(snapshot, cursorIndex, topMargin);
    
    // ✅ Transform caret from WASM world space to Canvas CSS space
    // caret_map values are in world pixels (96 DPI), must transform to canvas CSS pixels
    const caretCanvasX = transform.worldToCanvasX(caretPos.x);
    const caretCanvasY = transform.worldToCanvasY(caretPos.y);
    const caretCanvasHeight = caretPos.height * transform.scale;
    
    debugLog('🎯 [CanvasV3] Updating caret:', {
      cursorIndex,
      worldPos: `(${caretPos.x.toFixed(2)}, ${caretPos.y.toFixed(2)})`,
      canvasPos: `(${caretCanvasX.toFixed(2)}, ${caretCanvasY.toFixed(2)})`,
      height: caretCanvasHeight.toFixed(2),
      transformScale: transform.scale.toFixed(6)
    });
    
    caretRef.current.setPosition({
      x: caretCanvasX,  // ✅ Canvas CSS space
      y: caretCanvasY,  // ✅ Canvas CSS space
      height: caretCanvasHeight  // ✅ Canvas CSS space
    });
  };
  
  // ✅ Draw selection only (called when selection changes)
  const drawSelection = () => {
    const snapshot = currentSnapshotRef.current;
    const transform = transformRef.current;
    const overlay = overlayCanvasRef.current;
    const selectionRenderer = selectionRendererRef.current;
    
    if (!snapshot || !transform || !overlay || !selectionRenderer) {
      return;
    }
    
    const ctx = overlay.getContext('2d');
    if (!ctx) return;
    
    const state = inputHandler.getCurrentState();
    const selection = state.selection;
    
    // Clear and draw selection
    ctx.save();
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, overlay.width, overlay.height);
    ctx.restore();
    
    if (selection && selection.anchor !== selection.focus) {
      selectionRenderer.render(selection, snapshot, transform.scale);
      lastSelectionRef.current = { anchor: selection.anchor, focus: selection.focus };
    } else {
      lastSelectionRef.current = null;
    }
  };
  
  // ✅ Track last caret position to enable selective clearing (for blinks only)
  const lastCaretPosRef = useRef<{ x: number; y: number; height: number } | null>(null);
  // ✅ Draw caret (for full redraws) - assumes canvas already cleared
  const drawCaret = () => {
    const caret = caretRef.current;
    const overlay = overlayCanvasRef.current;
    
    if (!caret || !overlay) return;
    
    const ctx = overlay.getContext('2d');
    if (!ctx) return;
    
    if (caret['isActive'] && caret['visible']) {
      const pos = caret['position'];
      const config = caret['config'];
      const scale = caret['scale'];
      const dpr = caret['dpr'];
      
      ctx.save();
      ctx.setTransform(scale * dpr, 0, 0, scale * dpr, 0, 0);
      ctx.strokeStyle = config.color;
      ctx.lineWidth = config.width;
      ctx.beginPath();
      ctx.moveTo(pos.x, pos.y);
      ctx.lineTo(pos.x, pos.y + pos.height);
      ctx.stroke();
      ctx.restore();
      
      // Update last position for blink clearing
      lastCaretPosRef.current = { x: pos.x, y: pos.y, height: pos.height };
    }
  };
  
  // 🐛 DEBUG DASHBOARD: Helper to get grapheme count
  // 🐛 DEBUG DASHBOARD v3: Wrapper for extracted debug dashboard renderer
  // The implementation has been moved to DebugDashboardRenderer.ts for better maintainability
  const drawDebugDashboard = () => {
    const overlay = overlayCanvasRef.current;
    if (!overlay) return;

    renderDebugDashboard({
      overlayCanvas: overlay,
      currentSnapshot: currentSnapshotRef.current,
      inputHandler,
      typesetterClient,
      sharedInputContext,
      scale,
      snapshotVersion: snapshotVersionRef.current
    });
  };

  // 🐛 DEBUG OVERLAYS: Wrapper for extracted debug overlay renderer
  // The implementation has been moved to DebugOverlayRenderer.ts for better maintainability
  const drawOverlays = () => {
    const overlay = overlayCanvasRef.current;
    const snapshot = currentSnapshotRef.current;
    if (!overlay || !snapshot) return;

    renderDebugOverlays({
      overlayCanvas: overlay,
      currentSnapshot: snapshot,
      debugSettings: overlaySettingsRef.current,
      documentText: documentState?.text || '',
      affectedParagraphs: affectedParagraphsRef.current,
      inputHandler,
      typesetterClient,
      sharedInputContext,
      scale,
      snapshotVersion: snapshotVersionRef.current
    });
  };

  // Set ref for drawOverlays function
  drawOverlaysRef.current = drawOverlays;

  // ✅ Full redraw (selection + caret + debug overlays)
  // 🔬 OVERLAY VERIFICATION: Track if overlay ever draws text (it should NOT)
  const overlayVerifiedRef = useRef(false);
  
  const renderOverlay = () => {
    const snapshot = currentSnapshotRef.current;
    const transform = transformRef.current;
    const overlay = overlayCanvasRef.current;
    const selectionRenderer = selectionRendererRef.current;
    const caret = caretRef.current;
    
    if (!snapshot || !transform || !overlay || !selectionRenderer || !caret) {
      return;
    }
    
    // 🔬 One-time verification that overlay does NOT draw text
    if (!overlayVerifiedRef.current) {
      overlayVerifiedRef.current = true;
      console.log("🔬 OVERLAY_VERIFY page=" + pageIndex + " draws=[caret,selection,debug] NOT_TEXT=true");
    }
    
    const ctx = overlay.getContext('2d');
    if (!ctx) return;
    
    // 1. Clear canvas once
    ctx.save();
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, overlay.width, overlay.height);
    ctx.restore();
    
    // 2. Draw debug overlays (if enabled)
    drawOverlays();
    
    // 3. Draw selection highlight (if any)
    const state = inputHandler.getCurrentState();
    const selection = state.selection;
    
    if (selection && selection.anchor !== selection.focus) {
      selectionRenderer.render(selection, snapshot, transform.scale);
    }
    
    // 4. Draw caret (if visible and active)
    drawCaret();
  };
  
  // ✅ Update selection highlight when selection changes
  const updateSelectionHighlight = () => {
    renderOverlay(); // Use unified renderer
    
    const state = inputHandler.getCurrentState();
    const selection = state.selection;
    if (selection && selection.anchor !== selection.focus) {
      debugLog('🎨 [CanvasV3] Selection rendered:', {
        anchor: selection.anchor,
        focus: selection.focus,
        range: `[${Math.min(selection.anchor, selection.focus)}, ${Math.max(selection.anchor, selection.focus)}]`
      });
    }
  };

  // Set refs for functions used in text editing handler
  renderOverlayRef.current = renderOverlay;

  // ✅ SIMPLIFIED: Caret position is now updated during render phase only
  // With shared input handler across multiple Canvas instances, we can't use
  // setOnCursorChange (only one callback survives). Instead:
  // 1. Cursor changes trigger renderTrigger update in SharedInputContext
  // 2. All Canvas instances re-render
  // 3. Each Canvas updates its caret during render (with page bounds check)
  // 
  // For navigation (arrow keys), we still need immediate update since text doesn't change.
  // We'll register ONE callback at page 0 only for navigation, but skip it for typing.
  useEffect(() => {
    // Only page 0 registers the cursor change callback to avoid overwrites
    if (pageIndex !== 0) {
      return;
    }
    
    inputHandler.setOnCursorChange((cursorIndex: number) => {
      // Store last known cursor position for all pages to reference
      lastKnownCursorPositionRef.current = cursorIndex;
      
      // 🚀 INCREMENTAL LAYOUT: Update last selection AFTER cursor movement
      lastSelectionRef.current = { anchor: cursorIndex, focus: cursorIndex };
      
      // Navigation only: trigger render for immediate caret update
      const currentTextLength = inputHandler.getCurrentState().text.length;
      const isNavigationOnly = currentTextLength === (currentDocumentTextRef.current?.length || 0);
      
      if (isNavigationOnly) {
        DEBUG_ENABLED && console.log(`🎯 [CanvasV3] Navigation detected - triggering render for cursor at ${cursorIndex}`);
        // Trigger a render for all pages to update their carets
        if (sharedInputContext?.setRenderTrigger) {
          sharedInputContext.setRenderTrigger(prev => prev + 1);
        }
      }
    });
  }, [inputHandler, pageIndex, sharedInputContext]);

  // 🧪 TEST SUITE: Incremental Layout Testing
  useEffect(() => {
    const cleanup = setupCanvasTestListeners({
      typesetterClientRef,
      currentDocumentTextRef,
      currentSnapshotRef,
      fontFamily,
      onDocumentTextChange
    });

    return cleanup;
  }, [fontFamily, onDocumentTextChange]);

  return (
    <div 
      style={{ position: 'relative', width: pageWidth, height: pageHeight, display: 'block', cursor: 'text' }}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseUp}
    >
      <canvas
        ref={canvasRef}
        width={pageWidth * scale}
        height={pageHeight * scale}
        className={`edgedocs-canvas-v3 ${className}`}
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: `${pageWidth}px`,
          height: `${pageHeight}px`,
          display: 'block',
          backgroundColor: '#ffffff'
        }}
      />
      <canvas
        ref={overlayCanvasRef}
        width={pageWidth * scale}
        height={pageHeight * scale}
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: `${pageWidth}px`,  // ✅ Add px units
          height: `${pageHeight}px`, // ✅ Add px units
          pointerEvents: 'none',
          zIndex: 1  // Ensure overlay appears above main canvas
        }}
      />
      {/* ✅ ClipboardBridge: Hidden contenteditable for native clipboard events */}
      <ClipboardBridge
        onPasteText={handlePasteText}
        getSelectedText={getSelectedText}
        onCutSelection={handleCutSelection}
      />
    </div>
  );
};

// ✅ PERFORMANCE OPTIMIZATION: Memoize canvas to prevent unnecessary re-renders
// With optimistic rendering, canvas should only re-render for critical state changes
export const EdgeDocsCanvasV3 = React.memo(EdgeDocsCanvasV3Component, (prevProps, nextProps) => {
  // ✅ CRITICAL FIX: With optimistic rendering, document text changes should NOT trigger re-renders
  // The canvas manages its own text state via optimistic updates and WASM incremental layout

  // Worker state changes - require canvas re-initialization
  if (prevProps.sharedWasmClient !== nextProps.sharedWasmClient) return false;
  if (prevProps.skipInitialization !== nextProps.skipInitialization) return false;

  // Viewport changes (page dimensions, scale) - these require canvas re-initialization
  if (prevProps.pageWidth !== nextProps.pageWidth) return false;
  if (prevProps.pageHeight !== nextProps.pageHeight) return false;
  if (prevProps.scale !== nextProps.scale) return false;

  // Page setup changes (margins, orientation, etc.) - require canvas re-initialization
  if (prevProps.pageSetup !== nextProps.pageSetup) return false;
  if (prevProps.documentState?.pageSetup !== nextProps.documentState?.pageSetup) return false;

  // ✅ CRITICAL: Allow re-renders when pagination data changes (needed for incremental updates)
  if (prevProps.pageData !== nextProps.pageData) return false;
  if (prevProps.paginationResult !== nextProps.paginationResult) return false;

  // ✅ BLOCK all other re-renders, especially document text changes
  // Optimistic rendering means the canvas updates itself without React re-renders

  return true; // Props are equivalent, skip re-render
});
